import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import axios from "axios";
import { FaCheck, FaChevronRight, FaUniversity, FaInfoCircle, FaCcAmex, FaCcDinersClub, FaCreditCard, FaUser, FaEnvelope, FaLock, FaWallet, FaTh } from "react-icons/fa";
import SuccessCheckmark from "./SuccessCheckmark";
import FozzilConfetti from "./FozzilConfetti";
import { supabase } from "../supabaseClient";
import PaymentsComingSoon from "./PaymentsComingSoon";

// Use public folder paths for logos
const logoDarkTheme = "/fpay-logos/FOZZIL PAY LOGO black theme.png";
const logoLightTheme = "/fpay-logos/FOZZIL_PAY_LOGO_white theme.png";

const API_BASE_RAW = process.env.REACT_APP_FPAY_API_URL || "https://fpaybeshadesecure.fozzilindustries.workers.dev";
const API_BASE = API_BASE_RAW.endsWith('/') ? API_BASE_RAW.slice(0, -1) : API_BASE_RAW;

// --- Animations ---
const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const scaleIn = keyframes`
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const ShimmerBox = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: ${props => props.$themeMode === 'dark'
    ? 'linear-gradient(90deg, #1c1c1e 25%, #2c2c2e 50%, #1c1c1e 75%)'
    : 'linear-gradient(90deg, #f2f2f7 25%, #e5e5ea 50%, #f2f2f7 75%)'};
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

// --- Robust Theme Detection ---
const useSystemTheme = () => {
  const [theme, setTheme] = useState(
    window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  );

  useEffect(() => {
    if (!window.matchMedia) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      const newTheme = e.matches ? "dark" : "light";
      setTheme(newTheme);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    } else {
      // Legacy support
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  return theme;
};

// --- Styled Components ---
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  height: -webkit-fill-available; // Safari fix
  background: rgba(0, 0, 0, 0.6); 
  backdrop-filter: blur(12px);
  display: flex;
  justify-content: center;
  align-items: flex-end; 
  z-index: 2147483647; 
  animation: ${fadeIn} 0.3s ease-out;

  @media (min-width: 768px) {
    align-items: center; 
  }
`;

const Sheet = styled.div`
  background: ${props => props.$themeMode === 'dark' ? '#000000' : '#ffffff'};
  width: 100%;
  max-width: 820px; // Increased width as requested
  border-radius: 24px 24px 0 0;
  box-shadow: 0 -10px 40px rgba(0,0,0,0.4);
  overflow: hidden;
  animation: ${slideUp} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: ${props => props.$themeMode === 'dark' ? '#ffffff' : '#000000'};
  transition: all 0.3s ease;

  @media (min-width: 768px) {
    border-radius: 28px;
    animation: ${scaleIn} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 40px 120px rgba(0,0,0,0.6);
  }
`;

const Header = styled.div`
  padding: 16px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: transparent;
`;

const LogoWrapper = styled.div`
  height: 28px;
  display: flex;
  align-items: center;
  
  @media (min-width: 768px) {
    height: 34px;
  }
  
  img {
    height: 100%;
    width: auto;
    object-fit: contain;
  }
`;

const CancelBtn = styled.button`
  background: ${props => props.$themeMode === 'dark' ? '#1c1c1e' : '#f2f2f7'};
  border: none;
  color: ${props => props.$themeMode === 'dark' ? '#ffffff' : '#000000'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 20px;
  border-radius: 20px;
  transition: all 0.2s;
  position: relative;
  z-index: 10;
  pointer-events: auto;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
    transform: scale(0.96);
  }
`;

const Content = styled.div`
  padding: 0 16px 24px 16px;
  max-height: 70vh;
  overflow-y: auto;
  
  @media (min-width: 768px) {
    padding: 0 32px 32px 32px;
    max-height: 60vh;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${props => props.$themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
  margin: 0;
`;

const RowItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
  cursor: pointer;
  
  &:active {
    background: ${props => props.$isExpanded ? 'transparent' : props.$themeMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'};
  }
`;

const RowHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px; // Reduced from 20px
  width: 100%;
`;

const RowLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #8e8e93;
  width: 80px; // Reduced from 100px
  text-transform: uppercase;
  padding-top: 4px;

  @media (min-width: 768px) {
    width: 90px;
    padding-bottom: 0;
  }
`;

const RowValue = styled.div`
  flex: 1;
  font-size: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .chevron {
    color: #c7c7cc;
    font-size: 14px;
    transform: ${props => props.$isExpanded ? 'rotate(90deg)' : 'none'};
    transition: transform 0.2s;
  }
`;

const ExpandedArea = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-left: 0;
  animation: ${fadeIn} 0.3s ease-out;

  @media (min-width: 768px) {
    padding-left: 120px;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 350px; // Standardized to match Card and Contact sections
`;

const StyledInput = styled.input`
  background: ${props => props.$themeMode === 'dark' ? '#1c1c1e' : '#f2f2f7'};
  border: 1px solid transparent;
  padding: 12px 20px;
  border-radius: 100px;
  color: inherit;
  font-size: 15px;
  width: 100%;
  outline: none;
  transition: border-color 0.2s;
  &:focus {
    border-color: #007aff;
  }
`;

const UpiInputWrapper = styled.div`
  position: relative;
  width: 100%;
  
  /* Reduce width on mobile only */
  @media (max-width: 768px) {
    width: 85%; /* Scaled down for mobile */
  }
`;

const TooltipContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #8e8e93;
  margin-top: 4px;
  
  .info-icon {
    cursor: help;
    position: relative;
    display: flex;
    align-items: center;
    &:hover .tooltip { display: block; }
  }
  
  .tooltip {
    display: none;
    position: absolute;
    bottom: 24px;
    left: 0;
    background: #333;
    color: #fff;
    padding: 8px 12px;
    border-radius: 8px;
    width: 220px;
    z-index: 100;
    font-weight: 400;
    line-height: 1.4;
    box-shadow: 0 4px 12px rgba(0,0,0,0.5);
  }
`;

const CardBox = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  position: relative;
  overflow: hidden;

  .art-container {
      position: relative;
      width: 52px;
      height: 34px;
      perspective: 1000px;
  }
  
  .art {
    width: 100%;
    height: 100%;
    border-radius: 6px;
    position: absolute;
    top: 0;
    left: 0;
    transition: all 0.5s cubic-bezier(0.19, 1, 0.22, 1);
    box-shadow: 0 4px 10px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  // Metallic Brushed Finish for Card
  .card-art {
    background: ${props => props.$themeMode === 'dark'
    ? 'linear-gradient(135deg, #bdc3c7 0%, #ecf0f1 45%, #ffffff 50%, #ecf0f1 55%, #bdc3c7 100%)' // Light Metal for Dark Theme
    : 'linear-gradient(135deg, #0f0f0f 0%, #2c2c2c 45%, #444444 50%, #2c2c2c 55%, #0f0f0f 100%)'}; // Dark Metal for Light Theme
    background-size: 200% 200%;
    border: 1px solid ${props => props.$themeMode === 'dark' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'};
    transform: ${props => props.$activeMethod === 'Card' ? 'rotateY(0deg)' : 'rotateY(-90deg)'};
    opacity: ${props => props.$activeMethod === 'Card' ? '1' : '0'};
    
    // Glossy overlay
    &::after {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.1) 45%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 55%, transparent 60%);
        pointer-events: none;
    }
  }

  // UPI Logo Art
  .upi-art {
    background: transparent;
    border: none;
    box-shadow: none;
    transform: ${props => props.$activeMethod === 'UPI' ? 'rotateY(0deg)' : props.$activeMethod === 'Netbanking' ? 'rotateY(-90deg)' : 'rotateY(90deg)'};
    opacity: ${props => props.$activeMethod === 'UPI' ? '1' : '0'};
    display: flex;
    align-items: center;
    justify-content: center;
    
    img, svg {
        width: 100%; // Maximize size within the art box
        height: auto;
        object-fit: contain;
        transform: scale(1.5); // Make it pop out more since no container
    }
  }

  // Netbanking Logo Art
  .netbanking-art {
    background: ${props => props.$themeMode === 'dark' ? '#000000' : '#ffffff'};
    border: none;
    box-shadow: none;
    transform: ${props => props.$activeMethod === 'Netbanking' ? 'rotateY(0deg)' : 'rotateY(90deg)'};
    opacity: ${props => props.$activeMethod === 'Netbanking' ? '1' : '0'};
    color: ${props => props.$themeMode === 'dark' ? '#fff' : '#000'};
    
    svg {
        font-size: 20px;
    }
  }

  // Wallet Logo Art
  .wallet-art {
    background: ${props => props.$themeMode === 'dark' ? '#000000' : '#ffffff'};
    transform: ${props => props.$activeMethod === 'Wallet' ? 'rotateY(0deg)' : 'rotateY(90deg)'};
    opacity: ${props => props.$activeMethod === 'Wallet' ? '1' : '0'};
    color: ${props => props.$themeMode === 'dark' ? '#fff' : '#000'};
    svg { font-size: 20px; }
  }

  
  .info {
    display: flex;
    flex-direction: column;
    flex: 1;
    .name { font-weight: 600; font-size: 16px; }
    .num { font-size: 13px; color: #8e8e93; }
  }
`;



const SwipeInstruction = styled.div`
  font-size: 10px;
  color: ${props => props.$themeMode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'};
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0.8;
  font-weight: 500;
  pointer-events: none;
  
  span {
    display: inline-block;
    animation: ${pulse} 2s infinite;
  }
`;

const TotalRow = styled.div`
  padding: 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .text {
    font-size: 18px;
    letter-spacing: -0.2px;
    .sub { font-size: 13px; color: #8e8e93; margin-top: 2px; font-weight: 500; }
  }
  
  .val {
    font-size: 32px;
    font-weight: 400;
    letter-spacing: -1px;
  }
`;

const Footer = styled.div`
  padding: 20px 32px 32px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;


const PayButton = styled.button`
  background: ${props => props.$themeMode === 'dark' ? '#ffffff' : '#000000'};
  color: ${props => props.$themeMode === 'dark' ? '#000000' : '#ffffff'};
  width: 130px; // Scaled down to half-ish for sleeker look
  padding: 14px;
  border-radius: 100px;
  font-size: 16px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1);

  &:active {
    transform: scale(0.98);
    opacity: 0.9;
  }
`;

const Loader = styled.div`
  height: 450px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;





const ErrorPopup = styled.div`
  position: absolute;
  top: 24px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  color: #ff3b30;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  gap: 6px;
  box-shadow: none;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
    background: transparent;
    color: #ff3b30;
    padding: 0;
    font-size: 13px;
    font-weight: 600;
    gap: 6px;
    box-shadow: none;
    z-index: 1000;
    animation: ${fadeIn} 0.2s ease-out;
    pointer-events: none;
  `;



const CardInputWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const InputWithIcon = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  max-width: 280px;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 16px;
  color: ${props => props.$themeMode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)'};
  font-size: 14px;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const StyledInputWithPadding = styled(StyledInput)`
  padding-left: 40px;
`;

const PhoneInputContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  align-items: stretch;
`;

const CustomDropdownContainer = styled.div`
  position: relative;
  min-width: 90px;
`;

const DropdownTrigger = styled.div`
  background: ${props => props.$themeMode === 'dark' ? '#1c1c1e' : '#f2f2f7'};
  padding: 12px 16px;
  border-radius: 100px;
  color: inherit;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;
  
  &:hover {
    background: ${props => props.$themeMode === 'dark' ? '#2c2c2e' : '#e5e5ea'};
  }

  ${props => props.$isOpen && css`
    border-color: #007aff;
    box-shadow: 0 0 0 1px #007aff;
  `}
`;

const DropdownList = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: ${props => props.$themeMode === 'dark' ? 'rgba(28, 28, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)'};
  backdrop-filter: blur(20px);
  border-radius: 18px;
  width: 160px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.3);
  max-height: 240px;
  overflow-y: auto;
  z-index: 1000;
  padding: 6px;
  overflow-x: hidden;
  animation: ${fadeIn} 0.2s ease-out;
  border: 1px solid ${props => props.$themeMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'};

  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.$themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
    border-radius: 10px;
  }
`;

const DropdownItem = styled.div`
  padding: 6px 14px;
  border-radius: 100px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  color: ${props => props.$selected ? '#007aff' : 'inherit'};
  background: ${props => props.$selected ? (props.$themeMode === 'dark' ? 'rgba(0,122,255,0.1)' : 'rgba(0,122,255,0.05)') : 'transparent'};

  &:hover {
    background: ${props => props.$themeMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
  }
`;

const CardLogo = styled.div`
  position: absolute;
  right: 16px;
  font-size: 24px;
  display: flex;
  align-items: center;
  color: ${props => props.$themeMode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)'};
  pointer-events: none;
  transition: all 0.3s ease;
  
  svg {
    color: ${props => props.$active ? props.$color : 'inherit'};
  }
`;

// New Visa Logo SVG - supports dark theme
const VisaSVG = ({ dark }) => (
  <svg width="32" height="20" viewBox="0 0 750 471" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M278.198 334.228L311.058 138.997H364.781L331.887 334.228H278.198Z" fill={dark ? "#FFFFFF" : "#1A1F71"} />
    <path d="M524.307 142.687C513.189 138.465 495.574 133.968 473.861 133.968C420.789 133.968 384.066 161.143 383.780 199.992C383.506 228.996 410.004 245.185 429.580 254.979C449.670 265.047 456.446 271.583 456.359 280.659C456.197 294.472 439.562 300.804 424.068 300.804C402.541 300.804 390.999 297.619 373.095 289.831L366.172 286.552L358.544 332.098C371.325 337.709 394.262 342.592 418.069 342.854C474.608 342.854 510.541 316.058 510.934 274.628C511.113 251.700 496.665 234.070 467.028 219.486C448.948 210.048 437.894 203.840 438.005 194.099C438.005 185.428 448.182 176.161 470.076 176.161C488.472 175.868 501.947 179.933 512.474 184.155L517.610 186.612L525.104 142.413L524.307 142.687Z" fill={dark ? "#FFFFFF" : "#1A1F71"} />
    <path d="M611.064 138.997C623.832 138.997 633.356 142.456 639.574 155.663L713.529 334.228H658.094L647.813 305.632H580.881L573.393 334.228H518.609L572.313 152.040C577.639 139.697 588.253 138.997 611.064 138.997ZM596.147 208.095L578.046 265.535H624.248L596.147 208.095Z" fill={dark ? "#FFFFFF" : "#1A1F71"} />
    <path d="M206.002 138.997L153.529 269.413L147.886 240.900C138.317 208.666 108.361 173.699 75.094 156.220L123.282 334.092L180.217 334.021L263.030 138.997H206.002Z" fill={dark ? "#FFFFFF" : "#1A1F71"} />
    <path d="M117.298 138.997H33.893L33.198 142.990C101.285 159.974 146.722 201.058 166.066 250.929L146.187 155.898C142.802 143.020 132.936 139.403 117.298 138.997Z" fill="#F7A600" />
  </svg>
);

const MastercardSVG = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="12" r="7" fill="#EB001B" />
    <circle cx="15" cy="12" r="7" fill="#F79E1B" fillOpacity="0.8" />
  </svg>
);

// RuPay Logo SVG - Official wordmark style with dark theme support
const RupaySVG = ({ dark }) => (
  <svg width="48" height="18" viewBox="0 0 120 45" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* "Ru" text */}
    <text x="2" y="32" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="28" fill={dark ? "#FFFFFF" : "#1C65B0"}>Ru</text>
    {/* "Pay" text */}
    <text x="42" y="32" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="28" fill="#F48520">Pay</text>
    {/* Top swoosh - orange */}
    <path d="M95 8c6 0 11 2 15 5l-2 3c-3-2-7-4-13-4-5 0-10 2-13 5l-2-3c4-4 9-6 15-6z" fill="#F48520" />
    {/* Bottom swoosh - green */}
    <path d="M95 37c-6 0-11-2-15-5l2-3c3 2 7 4 13 4 5 0 10-2 13-5l2 3c-4 4-9 6-15 6z" fill="#097A44" />
  </svg>
);

const securePulse = keyframes`
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(0.98); }
  100% { opacity: 1; transform: scale(1); }
`;

const MaintenanceBanner = styled.div`
  background: ${props => props.$themeMode === 'dark' ? 'rgba(255, 159, 10, 0.1)' : 'rgba(255, 159, 10, 0.08)'};
  border: 1px solid rgba(255, 159, 10, 0.3);
  color: #ff9f0a;
  padding: 14px 20px;
  margin: 12px 20px;
  border-radius: 16px;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: ${fadeIn} 0.5s ease;

  svg {
    font-size: 18px;
    flex-shrink: 0;
  }
`;

// --- Fozzil Pay Secure Wrapper Styles ---
const FozzilPaySecureWrapper = styled.div`
  width: 100%;
  max-width: 500px;
  background: ${props => props.$themeMode === 'dark' ? '#1c1c1e' : '#ffffff'};
  border: 1px solid ${props => props.$themeMode === 'dark' ? 'rgba(0, 209, 255, 0.2)' : 'rgba(0, 209, 255, 0.1)'};
  border-radius: 24px;
  overflow: hidden;
  box-shadow: ${props => props.$themeMode === 'dark' ? '0 40px 100px rgba(0, 0, 0, 0.6)' : '0 20px 60px rgba(0, 0, 0, 0.15)'};
  margin: 10px auto;
  position: relative;
  transition: all 0.5s cubic-bezier(0.19, 1, 0.22, 1);
`;

const SecureHeader = styled.div`
  padding: 16px 24px;
  background: ${props => props.$themeMode === 'dark' ? 'rgba(0, 209, 255, 0.08)' : 'rgba(0, 209, 255, 0.04)'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-bottom: 1px solid ${props => props.$themeMode === 'dark' ? 'rgba(0, 209, 255, 0.1)' : 'rgba(0, 209, 255, 0.05)'};
  color: #00d1ff;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  .status-dot {
    width: 6px;
    height: 6px;
    background: #0033ffff;
    border-radius: 50%;
    box-shadow: 0 0 10px #0033ffff;
    animation: ${securePulse} 2s infinite;
  }
  
  svg {
    font-size: 14px;
    filter: drop-shadow(0 0 8px rgba(17, 0, 255, 0.5));
  }
`;
// Internal component for safety timeout
const SafetyLoaderTimeout = ({ method, countRef, setLoading }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      // If still loading after 5 seconds...
      // Check if this is the UPI success case (which we WANT to stay masked)
      if (method === 'UPI' && countRef.current > 1) {
        return; // Do nothing, keep masked
      }

      // Otherwise, force unmask
      setLoading(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [method, countRef, setLoading]);
  return null;
};

const FozzilSecureLoader = styled.div`
  position: absolute;
  top: 50px; /* Below header */
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.$themeMode === 'dark' ? '#1c1c1e' : '#ffffff'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 5;
  transition: opacity 0.5s ease;
  opacity: ${props => props.$isVisible ? 1 : 0};
  pointer-events: ${props => props.$isVisible ? 'auto' : 'none'};
`;

const OtpFrameWrapper = styled.div`
  width: 100%;
  height: 520px;
  background: white; 
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.5s ease;
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
    opacity: ${props => props.$isLoading ? 0 : 1};
    transition: opacity 0.8s ease-in-out;
  }
`;

const FozzilPayModal = ({
  isOpen,
  onClose,
  amount = "0.00",
  user_id = "guest",
  productinfo = "Pro Plan",
  firstname: initialFirstname = "",
  email: initialEmail = "",
  onSuccess,
  allowedMethods = ['Card', 'UPI', 'Netbanking', 'Wallet'],
  isRecurring = false,
  initialMethod = null
}) => {
  // PAYMENTS COMING SOON — intercept before main render
  const PAYMENTS_ENABLED = false;

  // Determine if this is a subscription/recurring payment based on explicit prop OR product name
  const isSubscription = isRecurring || (productinfo && /intelligence|subscription/i.test(productinfo));

  const effectiveMethods = isSubscription 
    ? allowedMethods.filter(m => m !== 'Netbanking' && m !== 'Wallet' && m !== 'Net Banking')
    : allowedMethods;

  const targetInit = initialMethod === 'bank' ? 'Netbanking' : initialMethod === 'upi' ? 'UPI' : initialMethod === 'card' ? 'Card' : initialMethod;
  const validTargetInit = targetInit && effectiveMethods.includes(targetInit) ? targetInit : null;

  const themeMode = useSystemTheme();
  const [step, setStep] = useState('review');
  const [method, setMethod] = useState(validTargetInit || (effectiveMethods[0] || 'Card'));
  const [expandedSection, setExpandedSection] = useState(null);
  const [otpValue, setOtpValue] = useState('');
  const [activeTxn, setActiveTxn] = useState('');
  const [mihpayid, setMihpayid] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);
  const [cardType, setCardType] = useState('unknown');
  const [cardError, setCardError] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({ code: '+91', flag: '🇮🇳', len: 10 });
  const [showCountryMenu, setShowCountryMenu] = useState(false);
  const [showBankMenu, setShowBankMenu] = useState(false);
  const [showWalletMenu, setShowWalletMenu] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [qrCodeHtml, setQrCodeHtml] = useState(null);

  const [isVerifyingVpa, setIsVerifyingVpa] = useState(false);
  const [vpaVerified, setVpaVerified] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [qrTimer, setQrTimer] = useState(300); // 5 minutes = 300 seconds
  const [selectedUpiApp, setSelectedUpiApp] = useState(null);
  const [showUpiAppSelector, setShowUpiAppSelector] = useState(false);
  const [payuError, setPayuError] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const pollingTimeoutRef = useRef(null); // For cleanup
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState("");
  const isPollingRef = useRef(false);
  const iframeLoadCountRef = useRef(0);

  // Saved Methods State
  const [savedMethods, setSavedMethods] = useState([]);
  const [selectedSavedMethod, setSelectedSavedMethod] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isLoadingMethods, setIsLoadingMethods] = useState(false);
  const [showSavedMethodsDropdown, setShowSavedMethodsDropdown] = useState(false);
  
  const savedMethodsDropdownRef = useRef(null);


  // Close dropdown on click outside or Esc
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (savedMethodsDropdownRef.current && !savedMethodsDropdownRef.current.contains(event.target)) {
        setShowSavedMethodsDropdown(false);
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setShowSavedMethodsDropdown(false);
      }
    };

    if (showSavedMethodsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showSavedMethodsDropdown]);

  // Strict enforcement: If recurring, force method to Card/UPI if it somehow drifted
  useEffect(() => {
    if (isSubscription && (method === 'Netbanking' || method === 'Wallet' || method === 'Net Banking')) {
      setMethod('Card');
      // If we have a saved Card, switch to it
      const savedCard = savedMethods.find(m => m.payment_type === 'Card');
      if (savedCard) {
        setSelectedSavedMethod(savedCard);
        setIsAddingNew(false);
        setExpandedSection(null);
      } else {
        setSelectedSavedMethod(null);
        setIsAddingNew(true);
        setExpandedSection('payment');
      }
    }
  }, [isSubscription, method, savedMethods]);

  // Detect Supabase Maintenance Status
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/payments/health`);
        if (res.data.status === 'maintenance') {
          setIsMaintenance(true);
          setMaintenanceMsg(res.data.message);
        } else {
          setIsMaintenance(false);
        }
      } catch (err) {
        if (err.response && err.response.status === 503) {
          setIsMaintenance(true);
          setMaintenanceMsg(err.response.data.message || "Scheduled maintenance is in progress.");
        }
      }
    };
    if (isOpen) {
      checkMaintenance();
    }
  }, [isOpen]);

  // Fetch Saved Payment Methods & Profile Details
  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !user_id || user_id === 'guest') return;
      
      setIsLoadingMethods(true);
      try {
        // Fetch saved methods
        const { data: methods } = await supabase
          .from('user_payment_methods')
          .select('*')
          .eq('user_id', user_id)
          .order('is_primary', { ascending: false });

        if (methods) {
          // Filter saved methods based on effective methods (handles Netbanking vs Net Banking mismatch)
          const validMethods = methods.filter(m => {
            const type = m.payment_type === 'Net Banking' ? 'Netbanking' : m.payment_type;
            return effectiveMethods.includes(type);
          });
          setSavedMethods(validMethods);
          
          if (validMethods.length > 0) {
            // Check if there is a saved method matching the requested initial Method
            const matchedSaved = validTargetInit ? validMethods.find(m => m.payment_type === validTargetInit || (m.payment_type === 'Net Banking' && validTargetInit === 'Netbanking')) : null;
            const primary = validMethods.find(m => m.is_primary) || validMethods[0];

            if (matchedSaved) {
              setSelectedSavedMethod(matchedSaved);
              setMethod(matchedSaved.payment_type);
              setIsAddingNew(false);
              setExpandedSection(null);
            } else if (validTargetInit) {
              // Priority given to initialMethod but no saved version found
              setSelectedSavedMethod(null);
              setMethod(validTargetInit);
              setIsAddingNew(true);
              setExpandedSection('payment');
            } else {
              // No initial method passed, auto-select primary saved method
              setSelectedSavedMethod(primary);
              setMethod(primary.payment_type);
              setIsAddingNew(false);
              setExpandedSection(null);
            }
          } else {
            // Default to 'Card' or 'initialMethod' for new users
            setMethod(validTargetInit || (isRecurring ? 'Card' : (effectiveMethods[0] || 'Card')));
            setIsAddingNew(true);
            setExpandedSection('payment');
          }
        }

        // Fetch profile for phone number
        const { data: profile } = await supabase
          .from('profiles')
          .select('phone, first_name, last_name, email')
          .eq('id', user_id)
          .single();

        if (profile) {
          setFormData(prev => ({
            ...prev,
            firstname: profile.first_name || (profile.first_name && profile.last_name ? `${profile.first_name} ${profile.last_name}` : prev.firstname),
            email: profile.email || prev.email,
            phone: profile.phone ? profile.phone.replace(/^\+91/, '') : prev.phone // Strip +91 for display
          }));
        }
      } catch (err) {
        console.error("Error fetching checkout data:", err);
      } finally {
        setIsLoadingMethods(false);
      }
    };

    fetchData();
    setShowSavedMethodsDropdown(false);
  }, [isOpen, user_id, isSubscription, validTargetInit]);
  const borderStyle = themeMode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.06)';

  // Sync cardType for saved methods
  useEffect(() => {
    if (!isAddingNew && selectedSavedMethod && selectedSavedMethod.payment_type === 'Card') {
      const name = selectedSavedMethod.display_name.toLowerCase();
      if (name.includes('visa')) setCardType('visa');
      else if (name.includes('mastercard')) setCardType('mastercard');
      else if (name.includes('amex')) setCardType('amex');
      else if (name.includes('diners')) setCardType('diners');
      else if (name.includes('rupay')) setCardType('rupay');
      else setCardType('unknown');
    }
  }, [selectedSavedMethod, isAddingNew]);

  // Sync ref with state for async access
  useEffect(() => {
    isPollingRef.current = isPolling;
  }, [isPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isPollingRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  // Refs for auto-focus
  const cardNumberRef = useRef(null);
  const expiryRef = useRef(null);
  const cvvRef = useRef(null);
  const cardNameRef = useRef(null);
  const firstNameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);

  // Swipe Logic Refs
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const isDragging = useRef(false);
  const wasSwiped = useRef(false);
  const minSwipeDistance = 40;


  // Data States
  const [formData, setFormData] = useState({
    card_number: '', expiry_date: '', cvv: '', card_name: '', upi_id: '', bank_name: '', 
    saveCard: productinfo === "Add Card Verification", saveUpi: false, saveNB: false, saveWallet: false,
    firstname: initialFirstname || '', email: initialEmail || '', phone: '', vpa: ''
  });
  
  // Dedicated state for Saved Card CVV
  const [savedCardCvv, setSavedCardCvv] = useState('');

  const [banks, setBanks] = useState([]);
  const [wallets, setWallets] = useState([]);

  // Fetch active instruments from PayU via Backend
  useEffect(() => {
    const fetchInstruments = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/payments/instruments`);
        if (res.data.success) {
          setBanks(res.data.banks || []);
          setWallets(res.data.wallets || []);
        }
      } catch (err) {
        // Fallback or handle error
      }
    };

    if (isOpen) {
      fetchInstruments();
    }
  }, [isOpen]);

  const WALLETS = wallets.length > 0 ? wallets : [
    { name: "Amazon Pay", code: "AMZPAY" },
  ];

  const BANKS = banks.length > 0 ? banks : [
    { name: "Airtel Payments Bank", code: "AIRNB", popular: true },
    { name: "AU Small Finance Bank", code: "AUSFNB", popular: true },
    { name: "AU Small Finance Bank - Corporate", code: "AUSFCNB", popular: true },
    { name: "AXIS Bank", code: "AXIB" },
    { name: "Bandhan Bank", code: "BANDNB", popular: true },
    { name: "Bank of India Corporate", code: "BOIB" },
    { name: "Bank of India Retail", code: "BOINB" },
    { name: "Bank of Maharashtra", code: "BOMB" },
    { name: "Canara Bank", code: "CABB" },
    { name: "Canara Bank (Erstwhile - Syndicate Bank)", code: "SYNDB" },
    { name: "Catholic Syrian Bank", code: "CSBN" },
    { name: "Central Bank Of India", code: "CBIB" },
    { name: "City Union Bank", code: "CUBB" },
    { name: "Cosmos Bank", code: "CSMSNB" },
    { name: "DCB Bank", code: "DCBB" },
    { name: "Deutsche Bank", code: "DSHB" },
    { name: "Dhanlaxmi Bank - Retail", code: "DLSB" },
    { name: "Federal Bank", code: "FEDB" },
    { name: "HDFC Bank", code: "HDFB" },
    { name: "ICICI Bank", code: "ICIB" },
    { name: "IDBI Bank", code: "IDBB" },
    { name: "IDBI Corp Netbanking", code: "IDBICORP" },
    { name: "IDFC FIRST Bank", code: "IDFCNB" },
    { name: "Indian Bank", code: "INDB" },
    { name: "Indian Bank (Erstwhile Allahabad Bank)", code: "ALLB", popular: true },
    { name: "Indian Overseas Bank", code: "INOB" },
    { name: "IndusInd Bank", code: "INIB" },
    { name: "Jammu & Kashmir Bank", code: "JAKB" },
    { name: "Jana Small Finance Bank", code: "JANANB" },
    { name: "Janata Sahakari Bank Pune", code: "JSBNB" },
    { name: "Karnataka Bank", code: "KRKB" },
    { name: "Karur Vysya - Corporate Banking", code: "KRVBC" },
    { name: "Karur Vysya Bank", code: "KRVB" },
    { name: "Kotak Mahindra Bank", code: "162B" },
    { name: "PNB (Erstwhile -Oriental Bank of Commerce)", code: "OBCB" },
    { name: "PNB (Erstwhile-United Bank of India)", code: "UNIB" },
    { name: "Punjab National Bank", code: "PNBB" },
    { name: "Punjab National Bank - Corporate Banking", code: "CPNB" },
    { name: "RBL Bank", code: "RBLNB" },
    { name: "Saraswat Bank", code: "SRSWT" },
    { name: "South Indian Bank", code: "SOIB" },
    { name: "Standard Chartered Bank", code: "SCBNB" },
    { name: "State Bank of India", code: "SBIB" },
    { name: "SVC Co-operative Bank Ltd.", code: "SVCNB" },
    { name: "Tamilnad Mercantile Bank", code: "TMBB" },
    { name: "UCO Bank", code: "UCOB" },
    { name: "Yes Bank", code: "YESB" }
  ].sort((a, b) => a.name.localeCompare(b.name));

  const POPULAR_BANKS = BANKS.filter(b => b.popular);

  const COUNTRIES = [
    { name: "India", code: "+91", flag: "🇮🇳", len: 10 },
    { name: "USA", code: "+1", flag: "🇺🇸", len: 10 },
    { name: "UK", code: "+44", flag: "🇬🇧", len: 10 },
    { name: "UAE", code: "+971", flag: "🇦🇪", len: 9 },
    { name: "Singapore", code: "+65", flag: "🇸🇬", len: 8 }
  ];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setStep('review');
      setExpandedSection('payment');
      setOtpValue('');
      setErrorMsg(null);
      setCardType('unknown');
      setCardError(false);
      setPaymentInitiated(false);

      // Detect mobile device
      const checkMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(checkMobile);
    }
    return () => { document.body.style.overflow = 'unset'; }
    return () => { document.body.style.overflow = 'unset'; }
  }, [isOpen]);

  // Force Auto-Select "Securely Save Card" for Verification Flow
  useEffect(() => {
    if (productinfo === "Add Card Verification") {
      setFormData(prev => ({ ...prev, saveCard: true }));
    } else {
      // Default to true for other card flows as well if preferred
      setFormData(prev => ({ ...prev, saveCard: true }));
    }
  }, [productinfo]);

  // QR Code timer countdown
  // QR Code & UPI Collect timer countdown
  useEffect(() => {
    const activeSteps = ['qr_code', 'redirect_html', 'upi_collect'];
    if (activeSteps.includes(step) && qrTimer > 0) {
      const timer = setTimeout(() => setQrTimer(qrTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (qrTimer === 0 && activeSteps.includes(step)) {
      // Timer expired
      setStep('failed');
      setPayuError('Transaction expired after 5 minutes. Please try again.');
      setIsPolling(false);
      setTimeout(() => {
        setStep('review');
        setQrCodeHtml(null);
        setQrTimer(300);
        setPayuError(null);
      }, 3000);
    }
  }, [step, qrTimer]);

  // Sync method with effectiveMethods if they change
  useEffect(() => {
    if (effectiveMethods && !effectiveMethods.includes(method)) {
      setMethod(effectiveMethods[0] || 'Card');
    }
  }, [effectiveMethods, method]);

  if (!isOpen) return null;

  const getFriendlyErrorMessage = (error) => {
    if (!error) return null;
    const str = String(error).toLowerCase();

    // Mask technical hash errors
    if (str.includes('hash') || str.includes('incorrectly calculated')) {
      return "Secure connection failed. Please try again in a moment.";
    }

    // Mask credential errors
    if (str.includes('key') || str.includes('salt') || str.includes('credential')) {
      return "Service unavailable. Our technical team has been notified.";
    }

    // Mask HTML/Internal errors
    if (str.includes('<html') || str.includes('<form') || str.includes('<!doctype')) {
      return "An unexpected error occurred. Please try again.";
    }

    // Handle timeout
    if (str.includes('timeout') || str.includes('expired')) {
      return "Session expired. Please restart the payment process.";
    }

    // Handled user-level messages
    if (str.includes('invalid upi id') || str.includes('vpa') || str.includes('otp')) {
      return error;
    }

    // If it's a very long string (vaguely a log), mask it
    if (error.length > 80) {
      return "Payment could not be completed. Please try again later.";
    }

    return error;
  };

  const getCardType = (number) => {
    const cleanNumber = number.replace(/\D/g, '');
    if (cleanNumber.startsWith('4')) return 'visa';
    if (/^5[1-5]/.test(cleanNumber)) return 'mastercard';
    if (/^3[47]/.test(cleanNumber)) return 'amex';
    if (/^3(?:0[0-5]|[68])/.test(cleanNumber)) return 'diners';
    if (/^(60|65|81|82|508)/.test(cleanNumber)) return 'rupay';
    return 'unknown';
  };

  const validateCard = (number) => {
    const clean = number.replace(/\D/g, '');
    if (clean.length < 13 || clean.length > 19) return false;
    let sum = 0;
    let shouldDouble = false;
    for (let i = clean.length - 1; i >= 0; i--) {
      let digit = parseInt(clean.charAt(i));
      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      shouldDouble = !shouldDouble;
    }
    return (sum % 10) === 0;
  };


  const handleInputChange = (field, value) => {
    let formattedValue = value;

    // Auto-format Card Number
    if (field === 'card_number') {
      formattedValue = value.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ').trim();
      const type = getCardType(formattedValue);
      setCardType(type);

      const clean = formattedValue.replace(/\D/g, '');
      const targetLen = type === 'amex' ? 15 : type === 'diners' ? 14 : 16;

      if (clean.length >= targetLen) {
        const isValid = validateCard(clean);
        setCardError(!isValid);
        if (!isValid) {
          setErrorMsg("Invalid card details");
          setTimeout(() => setErrorMsg(null), 3000);
        } else {
          // Auto-focus to expiry
          setTimeout(() => expiryRef.current?.focus(), 50);
        }
      } else {
        setCardError(false);
      }
    }

    // Auto-format Expiry Date
    if (field === 'expiry_date') {
      formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length > 2) {
        formattedValue = formattedValue.substring(0, 2) + '/' + formattedValue.substring(2, 4);
      }
      // Auto-focus to CVV when expiry is complete (5 chars: MM/YY)
      if (formattedValue.length >= 5) {
        setTimeout(() => cvvRef.current?.focus(), 50);
      }
    }

    // Auto-focus to Name when CVV is complete
    if (field === 'cvv') {
      const maxLen = cardType === 'amex' ? 4 : 3;
      if (value.length >= maxLen) {
        setTimeout(() => cardNameRef.current?.focus(), 50);
      }
    }

    if (field === 'phone') {
      const country = COUNTRIES.find(c => c.code === selectedCountry.code);
      formattedValue = value.replace(/\D/g, '').substring(0, country.len);
    }

    setFormData(prev => ({ ...prev, [field]: formattedValue }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  // Handle Enter key to submit payment
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handlePay();
    }
  };

  const validateField = (field, value) => {
    if (method === 'Card') {
      if (field === 'card_number' && (!value || value.replace(/\s/g, '').length < 13)) return true;
      if (field === 'expiry_date' && (!value || value.length < 5)) return true;
      if (field === 'cvv' && (!value || value.length < 3)) return true;
      if (field === 'card_name' && (!value || value.trim().length < 2)) return true;
    }
    if (field === 'firstname' && (!value || value.trim().length < 2)) return true;
    if (field === 'email' && (!value || !value.includes('@'))) return true;
    if (field === 'phone' && (!value || value.length < selectedCountry.len)) return true;
    return false;
  };

  const validateAllFields = () => {
    const errors = {};

    // Validate contact fields
    if (validateField('firstname', formData.firstname)) errors.firstname = true;
    if (validateField('email', formData.email)) errors.email = true;
    if (validateField('phone', formData.phone)) errors.phone = true;

    // Validate payment method specific fields
    if (method === 'Card') {
      if (validateField('card_number', formData.card_number)) errors.card_number = true;
      if (validateField('expiry_date', formData.expiry_date)) errors.expiry_date = true;
      if (validateField('cvv', formData.cvv)) errors.cvv = true;
      if (validateField('card_name', formData.card_name)) errors.card_name = true;
    } else if (method === 'Netbanking' || method === 'Wallet') {
      if (!formData.bank_name) errors.bank_name = true;
    }
    // UPI doesn't need validation - QR code is auto-generated

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const UPI_APPS = [
    { name: 'Google Pay', code: 'gpay', icon: '🟢', deepLink: 'gpay://upi/pay' },
    { name: 'PhonePe', code: 'phonepe', icon: '🟣', deepLink: 'phonepe://pay' },
    { name: 'BHIM', code: 'bhim', icon: '🔵', deepLink: 'bhim://pay' },
    { name: 'Paytm', code: 'paytm', icon: '🔷', deepLink: 'paytmmp://pay' },
    { name: 'Amazon Pay', code: 'amazonpay', icon: '⚫', deepLink: 'amazonpay://pay' },
    { name: 'WhatsApp', code: 'whatsapp', icon: '🟢', deepLink: 'whatsapp://pay' },
  ];

  const handleUpiAppSelect = async (app) => {
    setSelectedUpiApp(app);
    setShowUpiAppSelector(false);

    // For desktop, show UPI ID input popup
    const upiId = prompt(`Enter your ${app.name} UPI ID:`);
    if (!upiId || !upiId.includes('@')) {
      alert('Please enter a valid UPI ID (e.g. user@oxi)');
      return;
    }

    // Validate VPA with PayU
    try {
      setStep('processing'); // Show loader briefly
      const valRes = await axios.post(`${API_BASE}/api/payments/validate-vpa`, { vpa: upiId });
      // PayU validate_vpa response: status 'success' or 'failure', and 'message'
      if (valRes.data.status !== 'success') {
        alert('Invalid UPI ID. Please check and try again.');
        setStep('review');
        return;
      }
    } catch (err) {
      // Continue if API fails? Safe to block for quality, or allow fallback?
      // Let's block to prevent "Pardon".
      if (err.response && err.response.data && err.response.data.message) {
        alert("Validation Error: " + err.response.data.message);
      } else {
        alert("Could not validate UPI ID. Please try again.");
      }
      setStep('review');
      return;
    }

    // Update form data and proceed with payment
    setFormData(prev => ({ ...prev, upi_id: upiId }));
    await initiateUpiPayment(upiId);
  };

  const initiateUpiPayment = async (upiId = formData.upi_id) => {
    setStep('processing');
    setPayuError(null);

    try {
      // For mobile, try deep linking first
      if (isMobile && selectedUpiApp) {
        const deepLink = `${selectedUpiApp.deepLink}?pa=merchant@payu&pn=FOZZIL&am=${amount}&cu=INR&tn=${productinfo}`;
        window.location.href = deepLink;

        // Fallback to QR code after 2 seconds if app doesn't open
        setTimeout(() => {
          proceedWithQrCode();
        }, 2000);
      } else {
        await proceedWithQrCode();
      }
    } catch (err) {
      setPayuError(err.message || 'Failed to initiate UPI payment');
      setStep('review');
    }
  };

  const proceedWithQrCode = async () => {
    const txnid = `FP${Date.now()}${Math.random().toString(36).substr(2, 9)}`;
    setActiveTxn(txnid);

    const initResp = await axios.post(`${API_BASE}/api/payments/initiate`, {
      txnid,
      amount,
      productinfo,
      firstname: formData.firstname,
      email: formData.email,
      payment_mode: 'UPI',
      user_id,
      isRecurring
    });

    if (!initResp.data.success) {
      throw new Error(initResp.data.message || 'Failed to initiate payment');
    }

    // Log pending transaction
    saveTransactionRecord('pending');

    const procResp = await axios.post(`${API_BASE}/api/payments/process`, {
      txnid,
      amount,
      productinfo,
      firstname: formData.firstname,
      email: formData.email,
      phone: formData.phone,
      payment_mode: 'UPI',
      isRecurring
      // No upi_id needed - PayU generates QR code automatically
    });

    if (procResp.data.html) {
      setQrCodeHtml(procResp.data.html);
      setStep('qr_code');
      setQrTimer(300); // Reset to 5 minutes
      setMihpayid(procResp.data.mihpayid);
      startPollingPaymentStatus(txnid);
    } else if (procResp.data.error || !procResp.data.success) {
      throw new Error(procResp.data.message || procResp.data.error || 'Payment failed');
    }
  };



  const handleCycleMode = (direction = 'next') => {
    if (paymentInitiated) return;
    
    setMethod(current => {
      const idx = effectiveMethods.indexOf(current);
      let nextIdx;
      if (direction === 'next') {
        nextIdx = (idx + 1) % effectiveMethods.length;
      } else {
        nextIdx = (idx - 1 + effectiveMethods.length) % effectiveMethods.length;
      }
      const nextMethod = effectiveMethods[nextIdx];
      
      // Try to find a saved method for the next mode (handles Netbanking vs Net Banking mismatch)
      const saved = savedMethods.find(m => {
        const type = m.payment_type === 'Net Banking' ? 'Netbanking' : m.payment_type;
        return type === nextMethod;
      });
      if (saved) {
        setSelectedSavedMethod(saved);
        setIsAddingNew(false);
        setExpandedSection(null); // Collapse for saved methods
      } else {
        setSelectedSavedMethod(null);
        setIsAddingNew(true);
        setExpandedSection('payment'); // Auto-expand for new method inputs
      }
      
      return nextMethod;
    });
  };

  const handlePay = async () => {
    let currentMethod = method;
    let currentFormData = formData;

    // If using a saved method, skip most validations and just proceed
    if (!isAddingNew && selectedSavedMethod) {
      // Validate only contact info if needed
      const contactValid = (!validateField('firstname', formData.firstname)) &&
                           (!validateField('email', formData.email)) &&
                           (!validateField('phone', formData.phone));
      
      if (!contactValid) {
        setExpandedSection('contact');
        setErrorMsg("Please ensure contact details are correct");
        return;
      }

      // Safety Check: Ensure payment token exists
      if (!selectedSavedMethod.payment_token && selectedSavedMethod.payment_type !== 'UPI') {
         setErrorMsg("Invalid saved method. Please delete and re-add it.");
         return;
      }

      // Validate CVV for Saved Card
      if (selectedSavedMethod.payment_type === 'Card' && (!savedCardCvv || savedCardCvv.length < 3)) {
         setErrorMsg("Please enter CVV for saved card");
         return;
      }

      currentMethod = selectedSavedMethod.payment_type;
      currentFormData = {
        ...formData,
        payment_mode: currentMethod,
        cvv: savedCardCvv, // Use the specific saved card CVV
        vpa: currentMethod === 'UPI' ? selectedSavedMethod.metadata?.vpa : formData.vpa,
        bank_name: (currentMethod === 'Net Banking' || currentMethod === 'Wallet') ? 
                   (selectedSavedMethod.metadata?.bank || selectedSavedMethod.metadata?.provider) : formData.bank_name,
        payment_token: selectedSavedMethod.payment_token
      };

      // Sync state for UI consistency
      setMethod(currentMethod);
      setFormData(currentFormData);
    } else {
      // Validate all fields for new method
      const isValid = validateAllFields();

      if (!isValid) {
        setErrorMsg("Please fill in all required fields");
        setTimeout(() => setErrorMsg(null), 3000);

        let focused = false;
        const checkAndFocus = (field, ref) => {
          if (!focused && validateField(field, formData[field])) {
            if (['firstname', 'email', 'phone'].includes(field)) {
              setExpandedSection('contact');
              setTimeout(() => ref.current?.focus(), 100);
              focused = true;
            }
          }
        };

        checkAndFocus('firstname', firstNameRef);
        checkAndFocus('email', emailRef);
        checkAndFocus('phone', phoneRef);

        return;
      }
    }

    // Mark payment as initiated ONLY after validation passes
    setPaymentInitiated(true);

    // For UPI, initiate Collect Request
    if (currentMethod === 'UPI') {
      const vpaToUse = currentFormData.vpa;

      if (!vpaToUse || !vpaToUse.includes('@')) {
        setErrorMsg("Please enter a valid UPI ID");
        return;
      }

      // Validate VPA automatically if not already verified (skip for saved methods)
      if (!vpaVerified && isAddingNew) {
        setIsVerifyingVpa(true);
        try {
          const valRes = await axios.post(`${API_BASE}/api/payments/validate-vpa`, { vpa: vpaToUse });
          setIsVerifyingVpa(false);

          if (valRes.data.status !== 'success') {
            setErrorMsg("Invalid UPI ID - Could not verify");
            setTimeout(() => setErrorMsg(null), 3000);
            return;
          }
          setVpaVerified(true);
        } catch (err) {
          setIsVerifyingVpa(false);
          setErrorMsg("Could not verify UPI ID. Please check connection.");
          return;
        }
      }

      setStep('processing');
      setErrorMsg(null);

      try {
        const txnid = "FP" + Math.random().toString(36).substring(2, 10).toUpperCase();
        setActiveTxn(txnid);

        const initResp = await axios.post(`${API_BASE}/api/payments/initiate`, {
          txnid, amount, productinfo, user_id,
          payment_mode: currentMethod,
          firstname: currentFormData.firstname,
          email: currentFormData.email,
          isRecurring
        });

        if (!initResp.data.success) throw new Error("Initialization failed");

        const procResp = await axios.post(`${API_BASE}/api/payments/process`, {
          txnid, amount, productinfo, user_id,
          payment_mode: currentMethod,
          vpa: vpaToUse,
          isRecurring,
          ...currentFormData
        });

        if (procResp.data.success === false) {
          throw new Error(procResp.data.message || "Payment Failed");
        }

        if (procResp.data.redirectUrl) {
          setQrCodeHtml(`<iframe src="${procResp.data.redirectUrl}" style="width:100%; height:100%; border:none;" sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation"></iframe>`);
          setIframeLoading(true);
          setStep('redirect_html');
          setQrTimer(300);
          setMihpayid(procResp.data.mihpayid);
          startPollingPaymentStatus(txnid);
          return;
        }

        if (procResp.data.redirectHtml) {
          setQrCodeHtml(procResp.data.redirectHtml);
          setIframeLoading(true);
          setStep('redirect_html');
          setQrTimer(300);
          setMihpayid(procResp.data.mihpayid);
          startPollingPaymentStatus(txnid);
          return;
        }

        setStep('upi_collect');
        setQrTimer(300);
        setMihpayid(procResp.data.mihpayid);
        startPollingPaymentStatus(txnid);

      } catch (err) {
        setStep('failed');
        const errorDetail = err.response?.data?.message || err.message || "Payment Failed";
        setErrorMsg(errorDetail);
        setTimeout(() => {
          clearSensitiveDataOnly();
          setStep('review');
        }, 5000);
      }
      return;
    }

    setStep('processing');
    setErrorMsg(null);
    setOtpValue(''); 
    try {
      const txnid = "FP" + Math.random().toString(36).substring(2, 10).toUpperCase();
      setActiveTxn(txnid);

      const initResp = await axios.post(`${API_BASE}/api/payments/initiate`, {
        txnid, amount, productinfo, user_id,
        payment_mode: currentMethod,
        firstname: currentFormData.firstname,
        email: currentFormData.email,
        phone: currentFormData.phone,
        isRecurring
      });

      if (!initResp.data.success) throw new Error("Initialization failed");

      const payload = {
        txnid, amount, productinfo, user_id,
        payment_mode: currentMethod,
        isRecurring,
        ...currentFormData
      };

      const procResp = await axios.post(`${API_BASE}/api/payments/process`, payload);

      if (procResp.data.mihpayid) {
        setMihpayid(procResp.data.mihpayid);
      }

      // PRIORITIZE REDIRECTS (OTP/3DS) OVER IMMEDIATE SUCCESS
      if (procResp.data.redirectUrl) {
        setQrCodeHtml(`<iframe src="${procResp.data.redirectUrl}" style="width:100%; height:100%; border:none;" sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation"></iframe>`);
        setIframeLoading(true);
        setStep('redirect_html');
        setQrTimer(300);
        startPollingPaymentStatus(txnid);
        return;
      }

      if (procResp.data.redirectHtml) {
        setQrCodeHtml(procResp.data.redirectHtml);
        setIframeLoading(true);
        setStep('redirect_html');
        setQrTimer(300);
        startPollingPaymentStatus(txnid);
        return;
      }

      if (procResp.data.html || procResp.data.status === 'pending') {
         if (procResp.data.fallback) {
           const payUData = procResp.data.payUData;
           const targetUrl = procResp.data.payu_url || "https://secure.payu.in/_payment";
           const isNB = currentMethod === 'Netbanking';

           let formHtml = `<html><head><script>(function() { const n = function() {}; ['log','debug','info','warn','error'].forEach(m => { try { console[m] = n; } catch(e) {} }); })();</script></head><body onload="document.forms[0].submit()"><form method="POST" action="${targetUrl}" ${isNB ? 'target="_top"' : ''}>`
           Object.keys(payUData).forEach(key => {
             formHtml += `<input type="hidden" name="${key}" value="${payUData[key]}">`;
           });
           formHtml += `
                 </form>
                 <div style="text-align:center; padding-top:50px; font-family:sans-serif;">
                   Redirecting to secure bank page...
                 </div>
               </body>
             </html>
           `;

           setQrCodeHtml(formHtml);
           setIframeLoading(true);
           setStep('redirect_html');
           setQrTimer(300);
           startPollingPaymentStatus(txnid);
           return;
         }

         if (procResp.data.html) {
           let finalHtml = procResp.data.html;
           if (currentMethod === 'Netbanking' && finalHtml.includes('<form')) {
             finalHtml = finalHtml.replace(/<form/i, '<form target="_top"');
           }
           setQrCodeHtml(finalHtml);
           setIframeLoading(true);
           setStep('redirect_html');
           setQrTimer(180);
           setMihpayid(procResp.data.mihpayid);
           startPollingPaymentStatus(txnid);
           return;
         }
         // Fallback to OTP step if no HTML but status pending
         setStep('otp');
         return;
      }

      // ONLY IF NO REDIRECT, CHECK FOR SUCCESS
      if (procResp.data.success) {
        saveMethodToAccount(currentMethod, currentFormData);
        setShowConfetti(true);
        setStep('success');
        sessionStorage.setItem(`fozzil_payment_complete_${txnid}`, 'true');
        setTimeout(() => {
          onSuccess && onSuccess(procResp.data);
          resetPaymentForm();
          onClose && onClose();
        }, 5000);
        return;
      } else {
        throw new Error(procResp.data.message || procResp.data.error || 'Payment failed');
      }
    } catch (err) {
      setStep('failed');
      const msg = err.response?.data?.message || err.message || "Payment Failed";
      setErrorMsg(msg);
      setPayuError(msg);
      setTimeout(() => {
        clearSensitiveDataOnly();
        setStep('review');
        setErrorMsg(null);
      }, 5000);
    }
  };
  const handleOTPVerify = async () => {
    if (otpValue.length < 4) return alert("Please enter valid OTP");
    setStep('processing');
    try {
      const response = await axios.post(`${API_BASE}/api/payments/verify-otp`, {
        otp: otpValue,
        txnid: activeTxn,
        mihpayid: mihpayid,
        ...formData
      });

      if (response.data.success) {
        // Save method to account if user chose to
        saveMethodToAccount(method, formData);

        // Trigger confetti celebration!
        setShowConfetti(true);
        setStep('success');

        // Mark payment as complete in session storage to prevent re-trigger
        sessionStorage.setItem(`fozzil_payment_complete_${activeTxn}`, 'true');

        // Trigger haptic feedback on mobile
        if (navigator.vibrate) {
          navigator.vibrate([100, 50, 100]);
        }

        setTimeout(() => {
          onSuccess && onSuccess({ txn_id: activeTxn, status: 'success' });
          // Stay in modal - no redirect
          onClose && onClose();
        }, 5000); // Show success for 5 seconds
      } else {
        throw new Error(response.data.message || "Invalid OTP");
      }
    } catch (err) {
      setStep('failed');
      setOtpValue(''); // Clear cache on failure
      const msg = err.response?.data?.message || err.message;
      setErrorMsg(msg);
      setPayuError(msg); // Ensure payuError is set for the failed screen branding

      setTimeout(() => {
        clearSensitiveDataOnly();
        setIframeLoading(true);
        setStep('review'); // Return to payment page after showing failed state
        setErrorMsg(null);
      }, 3500); // 3.5s branded transition
    }
  };

  const startPollingPaymentStatus = (txnid) => {
    setIsPolling(true);
    let pollCount = 0;
    const maxPolls = 100; // Increased max polls to cover longer durations if needed

    const poll = async () => {
      // Check ref instead of state to avoid stale closure issues
      if (!isPollingRef.current && pollCount > 0) return; // Stop if cancelled

      pollCount++;
      try {
        const statusResp = await axios.get(`${API_BASE}/api/payments/${txnid}`);

        if (statusResp.data.success && statusResp.data.data) {
          const status = statusResp.data.data.status.toLowerCase();

          if (status === 'success' || status === 'captured') {
            // For Card Verification, manually trigger token capture (since PayU callbacks can't reach localhost)
            if (productinfo === "Add Card Verification" && method === 'Card' && user_id) {
              console.log(">>> Triggering manual token capture for card verification...");
              try {
                await axios.post(`${API_BASE}/api/payments/capture-token`, { txnid, user_id });
                console.log(">>> Token capture triggered successfully");
              } catch (tokenErr) {
                console.error(">>> Token capture failed:", tokenErr);
              }
            }
            
            // Save method to account if user chose to
            saveMethodToAccount(method, formData);
            // Log success transaction
            saveTransactionRecord('success', statusResp.data.data);
            
            // Immediately stop polling
            isPollingRef.current = false;
            setIsPolling(false);
            if (pollingTimeoutRef.current) {
              clearTimeout(pollingTimeoutRef.current);
              pollingTimeoutRef.current = null;
            }

            // Trigger confetti celebration!
            setShowConfetti(true);
            setStep('success');

            // Mark payment as complete in session storage to prevent re-trigger
            sessionStorage.setItem(`fozzil_payment_complete_${txnid}`, 'true');

            // Ensure IDs are available for the success screen
            if (statusResp.data.data.mihpayid) {
              setMihpayid(statusResp.data.data.mihpayid);
            }

            setTimeout(() => {
              onSuccess && onSuccess(statusResp.data.data);
              // Clear sensitive data before closing
              resetPaymentForm();
              // Stay in modal - no redirect
              onClose && onClose();
            }, 5000); // Show success for 5 seconds
            return;
          } else if (status === 'failed' || status === 'failure' || status === 'bounced' || status === 'cancelled' || status === 'cancel') {
            setIsPolling(false);
            setIframeLoading(true); // Persistently mask PayU if it reloads error page
            setStep('failed');
            const errorMsg = statusResp.data.data.error_message ||
              (status === 'cancelled' || status === 'cancel' ? 'Transaction cancelled by user' : 'Payment failed');
            setPayuError(errorMsg);
            
            // Log failed transaction
            saveTransactionRecord('failed', statusResp.data.data);

            // Stay in modal and return to payment form after 3.5 seconds
            setTimeout(() => {
              clearSensitiveDataOnly();
              setIframeLoading(true);
              setStep('review');
              setQrTimer(300);
            }, 3500); // 3.5s branded transition
            return;
          } else if (status === 'timeout' || status === 'expired') {
            setIsPolling(false);
            setIframeLoading(true); // Mask any gateway expiry pages
            setStep('failed');
            setPayuError('Payment session expired. Please try again.');
            // Log failed transaction
            saveTransactionRecord('failed', { error_message: 'Session Expired' });
            setTimeout(() => {
              clearSensitiveDataOnly();
              setIframeLoading(true);
              setStep('review');
              setQrTimer(300);
            }, 3500); // 3.5s branded transition
            return;
          }
        }
      } catch (err) {
        // Silent
      }

      if (pollCount >= maxPolls) {
        setIsPolling(false);
        setStep('failed');
        setPayuError('Payment timeout. Transaction expired after 5 minutes.');
        setTimeout(() => {
          setPayuError(null);
          setStep('review');
          setActiveTxn(null);
        }, 5000); // Show failure for 5 seconds
        return;
      }

      // Determine next interval (Faster detection for production)
      let nextInterval = 800;
      if (pollCount > 60) nextInterval = 3000; // Slower after ~1 minute

      if (isPollingRef.current) {
        pollingTimeoutRef.current = setTimeout(poll, nextInterval);
      }
    };

    // Start the first poll
    poll();
  };

  const resetPaymentForm = () => {
    // Clear all sensitive form data
    setFormData(prev => ({
      ...prev,
      card_number: '',
      expiry_date: '',
      cvv: '',
      card_name: '',
      upi_id: '',
      vpa: '',
      bank_name: '', // Reset bank/wallet selection
      firstname: '',
      email: '',
      phone: '',
      saveCard: false,
      saveUpi: false,
    }));

    // Reset internal states
    setVpaVerified(false);
    setValidationErrors({});
    setPaymentInitiated(false);
    setOtpValue('');
    setQrCodeHtml(null);
    setActiveTxn(null);
    setMihpayid(null);
    iframeLoadCountRef.current = 0; // Reset iframe counter
    setShowBankMenu(false);
    setShowWalletMenu(false);
    setShowCountryMenu(false);
    setPayuError(null);
  };

  const clearSensitiveDataOnly = () => {
    // Keep contact info, but clear payment specifics
    setFormData(prev => ({
      ...prev,
      card_number: '',
      expiry_date: '',
      cvv: '',
      card_name: '', // Optional: keep active card? Better to clear for security
      upi_id: '',
      vpa: '',
      // Do NOT clear firstname, email, phone
    }));
    setVpaVerified(false);
    setOtpValue('');
  };

  const saveTransactionRecord = async (status, txnDetails = {}) => {
    if (!user_id || user_id === 'guest') return;
    try {
      await supabase.from('user_transactions').insert([{
        user_id,
        amount: parseFloat(amount),
        status: status.toLowerCase(),
        method: method,
        txnid: activeTxn,
        mihpayid: txnDetails.mihpayid || mihpayid || null,
        product_info: productinfo,
        created_at: new Date().toISOString()
      }]);
    } catch (err) {
      console.error("Error saving transaction:", err);
    }
  };

  const saveMethodToAccount = async (processedMethod, processedData) => {
    if (!user_id || user_id === 'guest' || !isAddingNew) return;

    try {
      // Check for duplicate payment methods before saving
      const { data: existingMethods } = await supabase
          .from("user_payment_methods")
          .select("*")
          .eq("user_id", user_id)
          .eq("payment_type", processedMethod);

      if (existingMethods && existingMethods.length > 0) {
          for (const methodItem of existingMethods) {
              if (processedMethod === 'UPI' && processedData.vpa) {
                  if (methodItem.metadata?.vpa === processedData.vpa) return;
              }
              if (processedMethod === 'Card' && processedData.card_number) {
                  const last4 = processedData.card_number.replace(/\s/g, '').slice(-4);
                  if (methodItem.last_four === last4) return;
              }
              if (processedMethod === 'Netbanking' && processedData.bank_name) {
                  if (methodItem.provider === processedData.bank_name) return;
              }
              if (processedMethod === 'Wallet' && processedData.bank_name) {
                  if (methodItem.provider === processedData.bank_name) return;
              }
          }
      }

      const methodData = {
        user_id,
        payment_type: processedMethod === 'Netbanking' ? 'Net Banking' : (processedMethod === 'Wallet' ? 'Wallet' : processedMethod),
        display_name: processedMethod === 'Card' ? `${cardType.charAt(0).toUpperCase() + cardType.slice(1)} Card •••• ${processedData.card_number.slice(-4)}` :
                      processedMethod === 'UPI' ? `UPI - ${processedData.vpa}` :
                      (processedMethod === 'Netbanking' || processedMethod === 'Net Banking') ? BANKS.find(b => b.code === processedData.bank_name)?.name || 'Net Banking' :
                      processedMethod === 'Wallet' ? WALLETS.find(w => w.code === processedData.bank_name)?.name || 'Wallet' : 'Payment Method',
        last_four: processedMethod === 'Card' ? processedData.card_number.replace(/\s/g, '').slice(-4) : null,
        provider: (processedMethod === 'Netbanking' || processedMethod === 'Wallet' || processedMethod === 'Net Banking') ? processedData.bank_name : null,
        metadata: processedMethod === 'UPI' ? { vpa: processedData.vpa } : 
                  (processedMethod === 'Netbanking' || processedMethod === 'Net Banking') ? { bank: BANKS.find(b => b.code === processedData.bank_name)?.name } :
                  processedMethod === 'Wallet' ? { provider: WALLETS.find(w => w.code === processedData.bank_name)?.name } : {},
        is_primary: savedMethods.length === 0,
        payment_token: processedData.payment_token || null
      };

      if ((processedMethod === 'Card' && processedData.saveCard) || 
          (processedMethod === 'UPI' && processedData.saveUpi) || 
          ((processedMethod === 'Netbanking' || processedMethod === 'Net Banking') && processedData.saveNB) || 
          (processedMethod === 'Wallet' && processedData.saveWallet)) {
          await supabase.from('user_payment_methods').insert([methodData]);
      }
    } catch (err) {
      console.error("Error saving method to account:", err);
    }
  };

  const handleManualCancel = (e) => {
    e?.stopPropagation();
    e?.preventDefault();

    // If payment not initiated, just close directly
    if (!paymentInitiated) {
      resetPaymentForm();
      onClose();
      return;
    }

    // If payment initiated, show confirmation
    setShowCancelConfirm(true);
  };

  const confirmCancel = () => {
    setShowCancelConfirm(false);

    // Show branded FOZZIL cancellation screen
    setStep('cancelled');

    // Clear all form data using the helper
    resetPaymentForm();
    setIframeLoading(true); // Branded transition preparation

    // Stay on cancelled screen then go back to review (FOZZIL Integrated Experience)
    setTimeout(() => {
      setStep('review');
    }, 3500);
  };

  if (!PAYMENTS_ENABLED) {
    return <PaymentsComingSoon isOpen={isOpen} onClose={onClose} />;
  }

  return (
    <>
      {/* Celebratory Confetti - renders above everything on success */}
      <FozzilConfetti isActive={showConfetti} />

      <Overlay onClick={onClose}>
        <Sheet onClick={e => e.stopPropagation()} $themeMode={themeMode}>
          <Header $themeMode={themeMode}>
            <LogoWrapper>
              <img
                src={themeMode === 'dark' ? logoDarkTheme : logoLightTheme}
                alt="FOZZIL Pay"
              />
            </LogoWrapper>
            <CancelBtn onClick={handleManualCancel} $themeMode={themeMode}>
              {paymentInitiated ? 'Cancel' : 'Close'}
            </CancelBtn>
          </Header>

          {errorMsg && (
            <ErrorPopup>
              <FaInfoCircle /> {getFriendlyErrorMessage(errorMsg)}
            </ErrorPopup>
          )}

          {isMaintenance && (
            <MaintenanceBanner $themeMode={themeMode}>
              <FaInfoCircle />
              <span>{maintenanceMsg}</span>
            </MaintenanceBanner>
          )}

          <Divider $themeMode={themeMode} />

          {step === 'review' && (
            <>
              <Content>
                {/* Payment Section */}
                <RowItem $isExpanded={expandedSection === 'payment'} $themeMode={themeMode} onClick={() => setExpandedSection(expandedSection === 'payment' ? null : 'payment')}>
                  <RowHeader>
                    <RowLabel>Payment Method</RowLabel>
                    <RowValue $isExpanded={expandedSection === 'payment'}>
                      {!isAddingNew && selectedSavedMethod ? (
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '12px' }}>
                          <CardBox
                            $themeMode={themeMode}
                            $activeMethod={selectedSavedMethod.payment_type}
                            style={{ cursor: 'pointer', flex: 1 }}
                            onClick={(e) => {
                              // If not swiped, cycle mode on simple click
                              if (!wasSwiped.current) {
                                handleCycleMode('next');
                              }
                            }}
                            // Swipe Support for Saved Card
                            onTouchStart={(e) => {
                              touchEndX.current = 0;
                              touchStartX.current = e.targetTouches[0].clientX;
                            }}
                            onTouchMove={(e) => {
                              touchEndX.current = e.targetTouches[0].clientX;
                            }}
                            onTouchEnd={() => {
                              if (!touchStartX.current || !touchEndX.current) return;
                              const distance = touchStartX.current - touchEndX.current;
                              if (Math.abs(distance) > minSwipeDistance) {
                                handleCycleMode(distance > 0 ? 'next' : 'prev');
                              }
                            }}
                          >
                             <div className="art-container">
                               <div className="art card-art" style={{ 
                                 opacity: selectedSavedMethod.payment_type === 'Card' ? 1 : 0, 
                                 transform: selectedSavedMethod.payment_type === 'Card' ? 'rotateY(0deg)' : 'rotateY(-90deg)',
                                 padding: '4px'
                               }}>
                                 {cardType === 'visa' && <VisaSVG dark={themeMode !== 'dark'} />}
                                 {cardType === 'mastercard' && <MastercardSVG />}
                                 {cardType === 'amex' && <FaCcAmex style={{ fontSize: '24px', color: '#007bc1' }} />}
                                 {cardType === 'diners' && <FaCcDinersClub style={{ fontSize: '24px', color: '#0079be' }} />}
                                 {cardType === 'rupay' && <RupaySVG dark={themeMode !== 'dark'} />}
                                 {cardType === 'unknown' && <FaCreditCard style={{ fontSize: '20px', opacity: 0.5 }} />}
                               </div>
                              <div className="art upi-art" style={{ opacity: selectedSavedMethod.payment_type === 'UPI' ? 1 : 0, transform: selectedSavedMethod.payment_type === 'UPI' ? 'rotateY(0deg)' : 'rotateY(-90deg)' }}>
                                <img src="/fpay-logos/upi-logo.png" alt="UPI" style={{ width: '100%', height: 'auto', objectFit: 'contain' }} />
                              </div>
                              <div className="art netbanking-art" style={{ opacity: selectedSavedMethod.payment_type === 'Net Banking' ? 1 : 0, transform: selectedSavedMethod.payment_type === 'Net Banking' ? 'rotateY(0deg)' : 'rotateY(-90deg)' }}>
                                <FaUniversity />
                              </div>
                              <div className="art wallet-art" style={{ opacity: selectedSavedMethod.payment_type === 'Wallet' ? 1 : 0, transform: selectedSavedMethod.payment_type === 'Wallet' ? 'rotateY(0deg)' : 'rotateY(-90deg)' }}>
                                <FaWallet />
                              </div>
                            </div>
                            <div className="info">
                              <div className="name">
                                {selectedSavedMethod.display_name}
                                {selectedSavedMethod.is_primary && (
                                  <span style={{ 
                                    fontSize: '10px', 
                                    background: '#007aff', 
                                    color: 'white', 
                                    padding: '2px 6px', 
                                    borderRadius: '10px', 
                                    marginLeft: '8px',
                                    verticalAlign: 'middle'
                                  }}>Primary</span>
                                )}
                              </div>
                              <div className="num">
                                {selectedSavedMethod.payment_type === 'Card' ? `•••• ${selectedSavedMethod.last_four}` :
                                 selectedSavedMethod.payment_type === 'UPI' ? selectedSavedMethod.metadata?.vpa :
                                 selectedSavedMethod.payment_type === 'Net Banking' ? selectedSavedMethod.metadata?.bank :
                                 selectedSavedMethod.metadata?.provider || 'Saved Method'}
                              </div>
                              
                              {/* CVV Input for Saved Card */}
                              {selectedSavedMethod.payment_type === 'Card' && (
                                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                   <input
                                      type="password"
                                      placeholder="CVV"
                                      maxLength="4"
                                      value={savedCardCvv}
                                      onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '');
                                        setSavedCardCvv(val);
                                      }}
                                      onClick={(e) => e.stopPropagation()} // Prevent card cycling
                                      style={{
                                        width: '60px',
                                        padding: '6px 8px',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(128,128,128,0.3)',
                                        background: themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.8)',
                                        color: themeMode === 'dark' ? '#fff' : '#000',
                                        fontSize: '14px',
                                        textAlign: 'center',
                                        outline: 'none'
                                      }}
                                   />
                                   <span style={{ fontSize: '11px', color: themeMode === 'dark' ? '#aaa' : '#666' }}>Required</span>
                                </div>
                              )}

                              <SwipeInstruction $themeMode={themeMode}>
                                <span>&larr;</span> swipe <span>&rarr;</span>
                              </SwipeInstruction>
                            </div>
                          </CardBox>
                          <div 
                            ref={savedMethodsDropdownRef}
                            style={{ position: 'relative' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => setShowSavedMethodsDropdown(!showSavedMethodsDropdown)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#007aff',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}
                            >
                              CHANGE
                            </button>
                            {showSavedMethodsDropdown && (
                              <DropdownList $themeMode={themeMode} style={{ right: 0, left: 'auto', top: '35px', width: '220px', maxHeight: '200px' }}>
                                {savedMethods.map(m => (
                                  <DropdownItem
                                    key={m.id}
                                    $themeMode={themeMode}
                                    $selected={selectedSavedMethod?.id === m.id}
                                    onClick={() => {
                                      setSelectedSavedMethod(m);
                                      setMethod(m.payment_type);
                                      setIsAddingNew(false);
                                      setShowSavedMethodsDropdown(false);
                                      setExpandedSection(null); // Collapse when method selected
                                    }}
                                  >
                                    <div style={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {m.display_name}
                                    </div>
                                  </DropdownItem>
                                ))}
                                <div style={{ height: '1px', background: 'rgba(128,128,128,0.2)', margin: '4px 0' }} />
                                <DropdownItem
                                  $themeMode={themeMode}
                                   onClick={() => {
                                     setIsAddingNew(true);
                                     setSelectedSavedMethod(null);
                                     setShowSavedMethodsDropdown(false);
                                     setExpandedSection('payment'); // Open for new method inputs
                                   }}
                                  style={{ color: '#007aff', fontSize: '12px', fontWeight: 600, padding: '6px 14px' }}
                                >
                                  + Add payment method
                                </DropdownItem>
                              </DropdownList>
                            )}
                          </div>
                        </div>
                      ) : (
                        <CardBox
                          $themeMode={themeMode}
                          $activeMethod={method}
                          // Touch Events (Mobile)
                          onTouchStart={(e) => {
                            touchEndX.current = 0;
                            touchStartX.current = e.targetTouches[0].clientX;
                          }}
                          onTouchMove={(e) => {
                            touchEndX.current = e.targetTouches[0].clientX;
                          }}
                          onTouchEnd={() => {
                            if (!touchStartX.current || !touchEndX.current) return;
                            const distance = touchStartX.current - touchEndX.current;
                            if (Math.abs(distance) > minSwipeDistance) {
                              handleCycleMode(distance > 0 ? 'next' : 'prev');
                            }
                          }}
                          // Mouse Events (Desktop)
                          onMouseDown={(e) => {
                            isDragging.current = true;
                            wasSwiped.current = false;
                            touchStartX.current = e.clientX;
                            touchEndX.current = 0;
                          }}
                          onMouseMove={(e) => {
                            if (!isDragging.current) return;
                            touchEndX.current = e.clientX;
                          }}
                          onMouseUp={(e) => {
                            if (!isDragging.current) return;
                            isDragging.current = false;

                            // Check for swipe
                            if (touchEndX.current !== 0) {
                              const distance = touchStartX.current - touchEndX.current;
                              if (Math.abs(distance) > minSwipeDistance) {
                                wasSwiped.current = true; // Flag to stop onClick
                                handleCycleMode(distance > 0 ? 'next' : 'prev');
                              }
                            }
                          }}
                          onMouseLeave={() => {
                            isDragging.current = false;
                          }}
                          onClick={() => {
                            if (!wasSwiped.current) {
                              handleCycleMode('next');
                            }
                          }}
                        >
                          <div className="art-container">
                            <div className="art card-art" style={{ padding: '4px' }}>
                                {cardType === 'visa' && <VisaSVG dark={themeMode !== 'dark'} />}
                                {cardType === 'mastercard' && <MastercardSVG />}
                                {cardType === 'amex' && <FaCcAmex style={{ fontSize: '24px', color: '#007bc1' }} />}
                                {cardType === 'diners' && <FaCcDinersClub style={{ fontSize: '24px', color: '#0079be' }} />}
                                {cardType === 'rupay' && <RupaySVG dark={themeMode !== 'dark'} />}
                                {cardType === 'unknown' && <FaCreditCard style={{ fontSize: '20px', opacity: 0.5 }} />}
                              </div>
                            <div className="art upi-art">
                              <img src="/fpay-logos/upi-logo.png" alt="UPI" style={{ width: '85%', height: 'auto', objectFit: 'contain' }} />
                            </div>
                            <div className="art netbanking-art">
                              <FaUniversity />
                            </div>
                            <div className="art wallet-art">
                              <FaWallet />
                            </div>
                          </div>
                          <div className="info">
                            <div className="name">
                              {method === 'Card' ? (cardType === 'unknown' ? 'Credit/Debit Card' :
                                cardType === 'visa' ? 'Visa Card' :
                                  cardType === 'mastercard' ? 'Master Card' :
                                    cardType === 'amex' ? 'American Express Card' :
                                      cardType === 'diners' ? 'Diners Club Card' :
                                        cardType === 'rupay' ? 'RuPay Card' : 'Fozzil Card') :
                                method === 'UPI' ? 'UPI' :
                                  method === 'Netbanking' ? 'Net Banking' : 'FOZZIL Wallet'}
                            </div>
                            <div className="num">
                              {method === 'Card' ? (formData.card_number ? `•••• ${formData.card_number.replace(/\s/g, '').slice(-4)} ` : '•••• 4242') :
                                method === 'UPI' ? (formData.vpa || 'id@fozzilpay') :
                                  method === 'Netbanking' ? (formData.bank_name ? BANKS.find(b => b.code === formData.bank_name)?.name : 'Choose Bank') :
                                    (formData.bank_name ? WALLETS.find(w => w.code === formData.bank_name)?.name : 'Choose Wallet')}
                            </div>

                            <SwipeInstruction $themeMode={themeMode}>
                              <span>&larr;</span> swipe <span>&rarr;</span>
                            </SwipeInstruction>
                          </div>
                        </CardBox>
                      )}
                      <FaChevronRight className="chevron" />
                    </RowValue>
                  </RowHeader>

                  {expandedSection === 'payment' && (
                    <ExpandedArea onClick={e => e.stopPropagation()}>
                      {isAddingNew && method === 'Card' && (
                        <>
                          <InputGroup>
                            <CardInputWrapper>
                              <StyledInput
                                ref={cardNumberRef}
                                $themeMode={themeMode}
                                placeholder="Card Number"
                                maxLength="19"
                                value={formData.card_number}
                                onChange={e => handleInputChange('card_number', e.target.value)}
                                onBlur={e => validateField('card_number', e.target.value) && setValidationErrors(prev => ({ ...prev, card_number: true }))}
                                style={{
                                  borderColor: (cardError || validationErrors.card_number) ? '#ff3b30' : undefined,
                                  boxShadow: (cardError || validationErrors.card_number) ? '0 0 0 1px #ff3b30' : undefined
                                }}
                              />
                              <CardLogo $themeMode={themeMode} $active={cardType !== 'unknown'} $color={cardType === 'visa' ? '#1a1f71' : cardType === 'mastercard' ? '#eb001b' : cardType === 'amex' ? '#007bc1' : cardType === 'diners' ? '#0079be' : cardType === 'rupay' ? '#1c65b0' : 'inherit'}>
                                {cardType === 'visa' && <VisaSVG dark={themeMode === 'dark'} />}
                                {cardType === 'mastercard' && <MastercardSVG />}
                                {cardType === 'amex' && <FaCcAmex />}
                                {cardType === 'diners' && <FaCcDinersClub />}
                                {cardType === 'rupay' && <RupaySVG dark={themeMode === 'dark'} />}
                                {cardType === 'unknown' && <FaCreditCard />}
                              </CardLogo>
                            </CardInputWrapper>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <StyledInput
                                ref={expiryRef}
                                $themeMode={themeMode}
                                style={{
                                  maxWidth: '70px',
                                  padding: '12px 12px',
                                  textAlign: 'center',
                                  borderColor: validationErrors.expiry_date ? '#ff3b30' : undefined,
                                  boxShadow: validationErrors.expiry_date ? '0 0 0 1px #ff3b30' : undefined
                                }}
                                placeholder="MM/YY"
                                maxLength="5"
                                value={formData.expiry_date}
                                onChange={e => handleInputChange('expiry_date', e.target.value)}
                                onBlur={e => validateField('expiry_date', e.target.value) && setValidationErrors(prev => ({ ...prev, expiry_date: true }))}
                              />
                              <StyledInput
                                ref={cvvRef}
                                $themeMode={themeMode}
                                style={{
                                  maxWidth: '70px',
                                  padding: '12px 12px',
                                  textAlign: 'center',
                                  borderColor: validationErrors.cvv ? '#ff3b30' : undefined,
                                  boxShadow: validationErrors.cvv ? '0 0 0 1px #ff3b30' : undefined
                                }}
                                placeholder="CVV"
                                maxLength={cardType === 'amex' ? "4" : "3"}
                                type="password"
                                value={formData.cvv}
                                onChange={e => handleInputChange('cvv', e.target.value)}
                                onBlur={e => validateField('cvv', e.target.value) && setValidationErrors(prev => ({ ...prev, cvv: true }))}
                              />
                              <StyledInput
                                ref={cardNameRef}
                                $themeMode={themeMode}
                                placeholder="Name on Card"
                                value={formData.card_name}
                                onChange={e => handleInputChange('card_name', e.target.value)}
                                onBlur={e => validateField('card_name', e.target.value) && setValidationErrors(prev => ({ ...prev, card_name: true }))}
                                onKeyDown={handleKeyDown}
                                style={{
                                  borderColor: validationErrors.card_name ? '#ff3b30' : undefined,
                                  boxShadow: validationErrors.card_name ? '0 0 0 1px #ff3b30' : undefined
                                }}
                              />
                            </div>
                          </InputGroup>
                          <TooltipContainer>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: themeMode === 'dark' ? '#eee' : '#333' }}>
                              <input type="checkbox" checked={formData.saveCard} onChange={e => handleInputChange('saveCard', e.target.checked)} />
                              Securely save card as per RBI guidelines
                            </label>
                            <div className="info-icon">
                              <FaInfoCircle />
                              <div className="tooltip">Save your card details as per RBI policy. We use secure tokenization to protect your data.</div>
                            </div>
                          </TooltipContainer>
                        </>
                      )}
                      {isAddingNew && method === 'UPI' && (
                        <InputGroup>
                          <div style={{ maxWidth: '200px' }}></div>
                          <div style={{ marginBottom: '12px' }}>
                            <h4 style={{
                              margin: '0 0 16px 0',
                              fontSize: '14px',
                              fontWeight: 600,
                              color: themeMode === 'dark' ? '#fff' : '#000',
                              textAlign: 'left'
                            }}>
                              Enter UPI ID
                            </h4>

                            <div style={{ position: 'relative' }}>
                              <UpiInputWrapper>
                                <StyledInput
                                  $themeMode={themeMode}
                                  placeholder="Example: upi@fozzilpay"
                                  value={formData.vpa || ''}
                                  onChange={e => {
                                    handleInputChange('vpa', e.target.value);
                                    setVpaVerified(false); // Reset verification on change
                                  }}
                                  onKeyDown={handleKeyDown}
                                  style={{ paddingRight: '1px' }}
                                />
                                {/* Verify Button or Status */}
                                <div style={{
                                  position: 'absolute',
                                  right: '8px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  {isVerifyingVpa ? (
                                    <div style={{ fontSize: '11px', color: '#8e8e93' }}>Checking...</div>
                                  ) : vpaVerified ? (
                                    <div style={{
                                      color: '#34c759',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}>
                                      <FaCheck /> VERIFIED
                                    </div>
                                  ) : (formData.vpa && formData.vpa.includes('@')) && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation(); // Prevent modal click issues
                                        if (!formData.vpa || !formData.vpa.includes('@')) return;

                                        setIsVerifyingVpa(true);
                                        try {
                                          const res = await axios.post(`${API_BASE}/api/payments/validate-vpa`, { vpa: formData.vpa });
                                          if (res.data.status === 'success') {
                                            setVpaVerified(true);
                                          } else {
                                            setErrorMsg(res.data.message || "Invalid UPI ID");
                                            setTimeout(() => setErrorMsg(null), 3000);
                                          }
                                        } catch (err) {
                                          const msg = err.response?.data?.message || "Verification Failed - Service Unavailable";
                                          setErrorMsg(msg);
                                          console.error("VPA Verification Error:", err);
                                          setTimeout(() => setErrorMsg(null), 3500);
                                        } finally {
                                          setIsVerifyingVpa(false);
                                        }
                                      }}
                                      style={{
                                        background: themeMode === 'dark' ? '#3a3a3c' : '#e5e5ea',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '6px 10px',
                                        fontSize: '10px',
                                        fontWeight: '700',
                                        color: themeMode === 'dark' ? '#fff' : '#000',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      VERIFY
                                    </button>
                                  )}
                                </div>
                              </UpiInputWrapper>
                            </div>
                            <p style={{
                              fontSize: '11px',
                              color: '#8e8e93',
                              marginTop: '8px',
                              textAlign: 'center'
                            }}>
                              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={formData.saveUpi}
                                  onChange={e => handleInputChange('saveUpi', e.target.checked)}
                                />
                                Save my details for faster payments
                              </label>
                            </p>

                          </div>
                        </InputGroup>
                      )}
                      {!isAddingNew && selectedSavedMethod && (
                        <div style={{ 
                          padding: '6px 20px', 
                          background: themeMode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', 
                          borderRadius: '100px',
                          fontSize: '11px',
                          color: '#8e8e93',
                          textAlign: 'center',
                          width: 'fit-content',
                          margin: '0 auto 12px auto'
                        }}>
                          You are paying with your saved {selectedSavedMethod.display_name}.
                        </div>
                      )}
                      {isAddingNew && method === 'Netbanking' && (
                        <InputGroup>
                          <CustomDropdownContainer style={{ minWidth: '100%' }}>
                            <DropdownTrigger
                              $themeMode={themeMode}
                              $isOpen={showBankMenu}
                              onClick={() => setShowBankMenu(!showBankMenu)}
                              style={{
                                justifyContent: 'space-between',
                                border: validationErrors.bank_name ? '1px solid #ff3b30' : undefined,
                                boxShadow: validationErrors.bank_name ? '0 0 0 1px #ff3b30' : undefined
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <FaUniversity style={{ opacity: 0.5, fontSize: '16px' }} />
                                <span>{formData.bank_name ? BANKS.find(b => b.code === formData.bank_name)?.name : 'Select Bank'}</span>
                              </div>
                              <FaChevronRight style={{ transform: showBankMenu ? 'rotate(90deg)' : 'none', transition: 'transform 0.3s ease', opacity: 0.5 }} />
                            </DropdownTrigger>

                            {showBankMenu && (
                              <DropdownList $themeMode={themeMode} style={{ width: '100%', maxHeight: '300px' }}>
                                {/* Popular Banks Section */}
                                {POPULAR_BANKS.length > 0 && (
                                  <>
                                    <div style={{
                                      padding: '12px 16px',
                                      fontSize: '11px',
                                      fontWeight: '700',
                                      color: '#8e8e93',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px'
                                    }}>
                                      Popular Banks
                                    </div>
                                    {POPULAR_BANKS.map(b => (
                                      <DropdownItem
                                        key={`pop-${b.code}`}
                                        $themeMode={themeMode}
                                        $selected={b.code === formData.bank_name}
                                        onClick={() => {
                                          handleInputChange('bank_name', b.code);
                                          setShowBankMenu(false);
                                        }}
                                      >
                                        <FaUniversity style={{ opacity: 0.3 }} />
                                        <span style={{ fontWeight: 500 }}>{b.name}</span>
                                      </DropdownItem>
                                    ))}
                                    <div style={{
                                      height: '1px',
                                      background: themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                      margin: '8px 16px'
                                    }} />
                                  </>
                                )}

                                <div style={{
                                  padding: '12px 16px',
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  color: '#8e8e93',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  All Banks
                                </div>
                                {BANKS.map(b => (
                                  <DropdownItem
                                    key={b.code}
                                    $themeMode={themeMode}
                                    $selected={b.code === formData.bank_name}
                                    onClick={() => {
                                      handleInputChange('bank_name', b.code);
                                      setShowBankMenu(false);
                                    }}
                                  >
                                    <FaUniversity style={{ opacity: 0.3 }} />
                                    <span style={{ fontWeight: 500 }}>{b.name}</span>
                                    {b.code === formData.bank_name && <FaCheck style={{ marginLeft: 'auto', fontSize: '10px' }} />}
                                  </DropdownItem>
                                ))}
                              </DropdownList>
                            )}
                          </CustomDropdownContainer>
                          <label style={{ 
                             display: 'flex', 
                             alignItems: 'center', 
                             gap: '8px', 
                             marginTop: '12px', 
                             fontSize: '12px', 
                             color: themeMode === 'dark' ? '#8e8e93' : '#86868b',
                             cursor: 'pointer' 
                           }}>
                             <input 
                               type="checkbox" 
                               checked={formData.saveNB} 
                               onChange={e => handleInputChange('saveNB', e.target.checked)} 
                               style={{ cursor: 'pointer' }}
                             />
                             Save bank for future payments
                           </label>
                        </InputGroup>
                      )}
                      {isAddingNew && method === 'Wallet' && (
                        <InputGroup>
                          <CustomDropdownContainer style={{ minWidth: '100%' }}>
                            <DropdownTrigger
                              $themeMode={themeMode}
                              $isOpen={showWalletMenu}
                              onClick={() => setShowWalletMenu(!showWalletMenu)}
                              style={{
                                justifyContent: 'space-between',
                                border: validationErrors.bank_name ? '1px solid #ff3b30' : undefined,
                                boxShadow: validationErrors.bank_name ? '0 0 0 1px #ff3b30' : undefined
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <FaWallet style={{ opacity: 0.5, fontSize: '16px' }} />
                                <span>{formData.bank_name ? WALLETS.find(w => w.code === formData.bank_name)?.name : 'Select Wallet'}</span>
                              </div>
                              <FaChevronRight style={{ transform: showWalletMenu ? 'rotate(90deg)' : 'none', transition: 'transform 0.3s ease', opacity: 0.5 }} />
                            </DropdownTrigger>

                            {showWalletMenu && (
                              <DropdownList $themeMode={themeMode} style={{ width: '100%' }}>
                                {WALLETS.map(w => (
                                  <DropdownItem
                                    key={w.code}
                                    $themeMode={themeMode}
                                    $selected={w.code === formData.bank_name}
                                    onClick={() => {
                                      handleInputChange('bank_name', w.code);
                                      setShowWalletMenu(false);
                                    }}
                                  >
                                    <FaWallet style={{ opacity: 0.3 }} />
                                    <span style={{ fontWeight: 500 }}>{w.name}</span>
                                    {w.code === formData.bank_name && <FaCheck style={{ marginLeft: 'auto', fontSize: '10px' }} />}
                                  </DropdownItem>
                                ))}
                              </DropdownList>
                            )}
                          </CustomDropdownContainer>
                          <label style={{ 
                             display: 'flex', 
                             alignItems: 'center', 
                             gap: '8px', 
                             marginTop: '12px', 
                             fontSize: '12px', 
                             color: themeMode === 'dark' ? '#8e8e93' : '#86868b',
                             cursor: 'pointer' 
                           }}>
                             <input 
                               type="checkbox" 
                               checked={formData.saveWallet} 
                               onChange={e => handleInputChange('saveWallet', e.target.checked)} 
                               style={{ cursor: 'pointer' }}
                             />
                             Save wallet for future payments
                           </label>
                        </InputGroup>
                      )}
                    </ExpandedArea>
                  )}
                </RowItem>

                <Divider $themeMode={themeMode} />

                {/* Contact Section */}
                <RowItem $isExpanded={expandedSection === 'contact'} $themeMode={themeMode} onClick={() => setExpandedSection(expandedSection === 'contact' ? null : 'contact')}>
                  <RowHeader>
                    <RowLabel>Contact</RowLabel>
                    <RowValue $isExpanded={expandedSection === 'contact'}>
                      <div style={{ fontWeight: 400 }}>{formData.email}</div>
                      <FaChevronRight className="chevron" />
                    </RowValue>
                  </RowHeader>
                  {expandedSection === 'contact' && (
                    <ExpandedArea onClick={e => e.stopPropagation()}>
                      <InputGroup>
                        <InputWithIcon>
                          <InputIcon $themeMode={themeMode}>
                            <FaUser />
                          </InputIcon>
                          <StyledInputWithPadding
                            ref={firstNameRef}
                            $themeMode={themeMode}
                            style={{
                              maxWidth: '280px',
                              cursor: 'default',
                              borderColor: validationErrors.firstname ? '#ff3b30' : undefined,
                              boxShadow: validationErrors.firstname ? '0 0 0 1px #ff3b30' : undefined
                            }}
                            placeholder="Full Name"
                            value={formData.firstname}
                            readOnly={true}
                          />
                        </InputWithIcon>
                        <InputWithIcon>
                          <InputIcon $themeMode={themeMode}>
                            <FaEnvelope />
                          </InputIcon>
                          <StyledInputWithPadding
                            ref={emailRef}
                            $themeMode={themeMode}
                            style={{
                              maxWidth: '280px',
                              cursor: 'default',
                              borderColor: validationErrors.email ? '#ff3b30' : undefined,
                              boxShadow: validationErrors.email ? '0 0 0 1px #ff3b30' : undefined
                            }}
                            placeholder="Email"
                            type="email"
                            value={formData.email}
                            readOnly={true}
                          />
                        </InputWithIcon>
                        <PhoneInputContainer style={{ maxWidth: '280px' }}>
                          <CustomDropdownContainer>
                            <DropdownTrigger
                              $themeMode={themeMode}
                              $isOpen={false}
                              onClick={e => e.stopPropagation()}
                              style={{ cursor: 'default' }}
                            >
                              <span>{selectedCountry.flag}</span>
                              <span style={{ fontWeight: 600 }}>{selectedCountry.code}</span>
                            </DropdownTrigger>

                            {showCountryMenu && (
                              <DropdownList $themeMode={themeMode}>
                                {COUNTRIES.map(c => (
                                  <DropdownItem
                                    key={c.code}
                                    $themeMode={themeMode}
                                    $selected={c.code === selectedCountry.code}
                                    onClick={() => {
                                      setSelectedCountry(c);
                                      setFormData(prev => ({ ...prev, phone: '' }));
                                      setShowCountryMenu(false);
                                    }}
                                  >
                                    <span>{c.flag}</span>
                                    <span style={{ fontWeight: 500 }}>{c.code}</span>
                                    <span style={{ fontSize: '12px', opacity: 0.5, marginLeft: 'auto' }}>{c.name}</span>
                                  </DropdownItem>
                                ))}
                              </DropdownList>
                            )}
                          </CustomDropdownContainer>
                          <StyledInputWithPadding
                            ref={phoneRef}
                            $themeMode={themeMode}
                            style={{
                              paddingLeft: '12px',
                              cursor: 'default',
                              borderColor: validationErrors.phone ? '#ff3b30' : undefined,
                              boxShadow: validationErrors.phone ? '0 0 0 1px #ff3b30' : undefined
                            }}
                            type="tel"
                            placeholder="Phone Number"
                            value={formData.phone}
                            onChange={(e) => {
                              // Only allow numbers
                              const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                              handleInputChange('phone', val);
                            }}
                            maxLength={10}
                            readOnly={true}
                          />
                        </PhoneInputContainer>
                      </InputGroup>
                    </ExpandedArea>
                  )}
                </RowItem>

                <Divider $themeMode={themeMode} />

                <TotalRow>
                  <div className="text" style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {productinfo.toUpperCase()}
                      {isRecurring && (
                        <span style={{
                          fontSize: '10px',
                          background: 'rgba(76, 217, 100, 0.1)',
                          color: '#4cd964',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontWeight: '800',
                          letterSpacing: '0.5px'
                        }}>RECURRING</span>
                      )}
                    </div>
                    <div className="sub">FOZZIL PAY SECURE</div>
                  </div>
                  <div className="val">₹{amount}{isRecurring && <span style={{ fontSize: '14px', color: '#8e8e93', fontWeight: 500 }}>/mo</span>}</div>
                </TotalRow>
              </Content>

              <Footer>
                <PayButton 
                  onClick={handlePay} 
                  $themeMode={themeMode} 
                  disabled={isAddingNew && ((method === 'Card' && !formData.card_number) || (method === 'UPI' && !vpaVerified))}
                >
                  Pay
                </PayButton>
              </Footer>
            </>
          )}

          {step === 'otp' && (
            <>
              <Content>
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div style={{ fontSize: '24px', fontWeight: '600', marginBottom: '8px' }}>Verification Required</div>
                  <div style={{ color: '#8e8e93', fontSize: '14px', marginBottom: '32px' }}>
                    We've sent a 6-digit code to your registered mobile number ending in <span style={{ color: themeMode === 'dark' ? '#fff' : '#000', fontWeight: 'bold' }}>{formData.phone.slice(-4)}</span>
                  </div>

                  <div style={{ maxWidth: '300px', margin: '0 auto' }}>
                    <StyledInput
                      $themeMode={themeMode}
                      placeholder="Enter OTP"
                      maxLength="6"
                      value={otpValue}
                      onChange={e => setOtpValue(e.target.value)}
                      style={{ textAlign: 'center', fontSize: '24px', letterSpacing: '8px', padding: '16px' }}
                    />
                    <div style={{ marginTop: '16px', fontSize: '13px', color: '#007aff', cursor: 'pointer' }}>
                      Resend Code
                    </div>
                  </div>
                </div>
              </Content>
              <Footer>
                <PayButton onClick={handleOTPVerify} $themeMode={themeMode}>
                  Verify & Pay
                </PayButton>
                <div onClick={handleManualCancel} style={{ marginTop: '12px', cursor: 'pointer', fontSize: '14px', color: '#8e8e93', textAlign: 'center' }}>Cancel</div>
              </Footer>
            </>
          )}



          {step === 'upi_collect' && (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{
                background: 'transparent',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                textAlign: 'left'
              }}>
                <div style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src="/fpay-logos/upi-logo.png" alt="UPI" style={{ width: '100%', height: 'auto', objectFit: 'contain' }} />
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px', color: themeMode === 'dark' ? '#fff' : '#000' }}>Step 1</h4>
                  <p style={{ margin: 0, fontSize: '14px', color: '#8e8e93' }}>Go to <b>UPI mobile app</b></p>
                </div>
              </div>

              <div style={{
                background: 'transparent',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                textAlign: 'left'
              }}>
                <div style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FaTh size={28} color={themeMode === 'dark' ? '#fff' : '#000'} />
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px', color: themeMode === 'dark' ? '#fff' : '#000' }}>Step 2</h4>
                  <p style={{ margin: 0, fontSize: '14px', color: '#8e8e93' }}>Check pending requests and approve payment by entering <b>UPI PIN / Pass</b></p>
                </div>
              </div>

              <div style={{
                border: '1px solid #d1d1d6',
                borderRadius: '8px',
                padding: '12px',
                display: 'inline-block',
                color: '#ff3b30',
                fontWeight: 600,
                fontSize: '14px'
              }}>
                {isPolling
                  ? `Expires in : ${Math.floor(qrTimer / 60)}:${(qrTimer % 60).toString().padStart(2, '0')} `
                  : 'Transaction Processing...'}
              </div>
              <p style={{
                marginTop: '16px',
                fontSize: '12px',
                color: '#8e8e93',
                fontStyle: 'italic'
              }}>
                Do not press back or close this window.
              </p>
            </div>
          )}

          {step === 'redirect_html' && qrCodeHtml && (
            <div style={{ padding: '0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <FozzilPaySecureWrapper $themeMode={themeMode}>
                <SecureHeader $themeMode={themeMode}>
                  <div className="status-dot" />
                  <FaLock /> SECURE FOZZIL ENCRYPTION ACTIVE
                </SecureHeader>

                <FozzilSecureLoader $isVisible={iframeLoading} $themeMode={themeMode}>
                  <Loader>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ShimmerBox $themeMode={themeMode} />
                      <img
                        src={themeMode === 'dark' ? logoDarkTheme : logoLightTheme}
                        alt="Fozzil"
                        style={{
                          height: '24px',
                          position: 'absolute',
                          zIndex: 2
                        }}
                      />
                    </div>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      marginTop: '20px',
                      color: themeMode === 'dark' ? '#fff' : '#000'
                    }}>
                      Secure Connection Active
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#8e8e93',
                      marginTop: '8px',
                      textAlign: 'center',
                      maxWidth: '240px'
                    }}>
                      {iframeLoadCountRef.current > 1
                        ? "Finalizing your transaction. Please do not close this window."
                        : "Establishing a secure FOZZIL vault to your bank portal. Please wait..."}
                    </div>
                  </Loader>
                </FozzilSecureLoader>

                <OtpFrameWrapper $isLoading={iframeLoading}>
                  {qrCodeHtml.startsWith('<form') || qrCodeHtml.includes('<body') ? (
                    <iframe
                      title="Bank OTP"
                      srcDoc={qrCodeHtml}
                      onLoad={() => {
                        iframeLoadCountRef.current += 1;

                        // Mask initially to hide PayU branding/transitions
                        setIframeLoading(true);

                        // LOGIC:
                        // 1. UPI: 5s mask. Hides the success redirect completely while polling completes.
                        //    No user interaction needed on the iframe usually.
                        // 2. Card/NB: 3.5s mask. Hides the "Redirecting" splash but reveals OTP page.
                        const maskDuration = method === 'UPI' ? 5000 : 4000;

                        setTimeout(() => {
                          setIframeLoading(false);
                        }, maskDuration);
                      }}
                      sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation"
                    />
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{ __html: qrCodeHtml }}
                      style={{ height: '100%', overflow: 'auto' }}
                      ref={(el) => {
                        if (el && iframeLoading) {
                          setTimeout(() => setIframeLoading(false), 1000);
                        }
                      }}
                    />
                  )}
                </OtpFrameWrapper>
              </FozzilPaySecureWrapper>

              {/* Safety Timeout to prevent stuck loader */}
              {iframeLoading && step === 'redirect_html' && (
                <SafetyLoaderTimeout
                  method={method}
                  countRef={iframeLoadCountRef}
                  setLoading={setIframeLoading}
                />
              )}

              <p style={{
                fontSize: '12px',
                color: '#8e8e93',
                textAlign: 'center',
                marginTop: '12px',
                fontStyle: 'italic'
              }}>
                Please do not close this window. Your bank is verifying your identity.
              </p>
            </div>
          )}

          {step === 'processing' && (
            <Loader>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                <ShimmerBox $themeMode={themeMode} />
                <img
                  src={themeMode === 'dark' ? logoDarkTheme : logoLightTheme}
                  alt="Fozzil"
                  style={{
                    height: '28px',
                    position: 'absolute',
                    zIndex: 2
                  }}
                />
              </div>
              <div style={{ fontSize: 20, fontWeight: 500, color: themeMode === 'dark' ? '#fff' : '#000' }}>Processing...</div>
            </Loader>
          )}

          {step === 'success' && (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              <SuccessCheckmark />

              <h2 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: themeMode === 'dark' ? '#fff' : '#000',
                margin: '20px 0 8px 0'
              }}>
                Payment Completed
              </h2>

              <p style={{
                fontSize: '14px',
                color: '#8e8e93',
                marginBottom: '30px'
              }}>
                You have successfully paid ₹{amount}
              </p>

              {/* Payment Details */}
              <div style={{
                background: themeMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '24px',
                textAlign: 'left'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '16px',
                  paddingBottom: '16px',
                  borderBottom: `1px solid ${themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
                }}>
                  <span style={{ fontSize: '13px', color: '#8e8e93' }}>Paid On:</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: themeMode === 'dark' ? '#fff' : '#000' }}>
                    {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '16px',
                  paddingBottom: '16px',
                  borderBottom: `1px solid ${themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
                }}>
                  <span style={{ fontSize: '13px', color: '#8e8e93' }}>Payment ID:</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: themeMode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace' }}>
                    {mihpayid || 'N/A'}
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ fontSize: '13px', color: '#8e8e93' }}>Transaction ID:</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: themeMode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace' }}>
                    {activeTxn || 'N/A'}
                  </span>
                </div>
              </div>

              {/* Redirect message */}
              <div style={{
                background: 'rgba(0, 209, 255, 0.1)',
                border: '1px solid rgba(0, 209, 255, 0.3)',
                borderRadius: '12px',
                padding: '14px',
                fontSize: '13px',
                color: '#00d1ff',
                fontWeight: 500
              }}>
                Redirecting to FOZZIL in {Math.ceil((5000 - Date.now() % 5000) / 1000)} seconds...
              </div>
            </div>
          )}

          {step === 'failed' && (
            <Loader>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
                <ShimmerBox $themeMode={themeMode} />
                <img
                  src={themeMode === 'dark' ? logoDarkTheme : logoLightTheme}
                  alt="Fozzil"
                  style={{
                    height: '32px',
                    position: 'absolute',
                    zIndex: 2
                  }}
                />
              </div>

              <div style={{ fontSize: 24, fontWeight: 700, color: '#ff3b30', textAlign: 'center' }}>
                {payuError && String(payuError).toLowerCase().includes('timeout') ? 'Payment Expired' : 'Payment Failed'}
              </div>

              <div style={{
                fontSize: 16,
                fontWeight: 600,
                color: themeMode === 'dark' ? '#fff' : '#000',
                marginTop: 20,
                textAlign: 'center'
              }}>
                Taking you back to Payment Options ..
              </div>

              <div style={{
                fontSize: 12,
                color: '#8e8e93',
                marginTop: 12,
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}>
                DO NOT CLOSE THIS WINDOW
              </div>

              {payuError && (
                <div style={{
                  marginTop: '24px',
                  padding: '12px 16px',
                  background: 'rgba(255,59,48,0.05)',
                  borderRadius: '12px',
                  fontSize: '13px',
                  color: '#ff3b30',
                  maxWidth: '350px',
                  textAlign: 'center'
                }}>
                  {getFriendlyErrorMessage(payuError)}
                </div>
              )}
            </Loader>
          )}

          {showUpiAppSelector && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px'
            }}>
              <div style={{
                background: themeMode === 'dark' ? '#1c1c1e' : '#fff',
                borderRadius: '24px',
                padding: '30px',
                maxWidth: '400px',
                width: '100%',
                maxHeight: '80vh',
                overflow: 'auto'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ margin: 0, color: themeMode === 'dark' ? '#fff' : '#000' }}>Select UPI App</h3>
                  <button
                    onClick={() => setShowUpiAppSelector(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: themeMode === 'dark' ? '#fff' : '#000'
                    }}
                  >×</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  {UPI_APPS.map(app => (
                    <div
                      key={app.code}
                      onClick={() => handleUpiAppSelect(app)}
                      style={{
                        padding: '20px',
                        background: themeMode === 'dark' ? '#2c2c2e' : '#f2f2f7',
                        borderRadius: '16px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        ':hover': { transform: 'scale(1.05)' }
                      }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>{app.icon}</div>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: themeMode === 'dark' ? '#fff' : '#000'
                      }}>
                        {app.name}
                      </div>
                    </div>
                  ))}
                </div>
                <p style={{
                  marginTop: '20px',
                  fontSize: '12px',
                  color: '#8e8e93',
                  textAlign: 'center',
                  margin: '20px 0 0 0'
                }}>
                  You'll be asked to enter your UPI ID after selecting an app
                </p>
              </div>
            </div>
          )}
          {step === 'cancelled' && (
            <Loader>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
                <ShimmerBox $themeMode={themeMode} />
                <img
                  src={themeMode === 'dark' ? logoDarkTheme : logoLightTheme}
                  alt="Fozzil"
                  style={{
                    height: '34px',
                    position: 'absolute',
                    zIndex: 2
                  }}
                />
              </div>

              <div style={{ fontSize: 24, fontWeight: 700, color: '#ff0000ff', textAlign: 'center' }}>
                Payment Cancelled
              </div>

              <div style={{
                fontSize: 16,
                fontWeight: 600,
                color: themeMode === 'dark' ? '#fff' : '#000',
                marginTop: 20,
                textAlign: 'center'
              }}>
                Taking you back to Payment Options ..
              </div>

              <div style={{
                fontSize: 12,
                color: '#8e8e93',
                marginTop: 12,
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                textAlign: 'center'
              }}>
                DO NOT CLOSE THIS WINDOW
              </div>
            </Loader>
          )}
        </Sheet >
      </Overlay >

      {/* Cancel Confirmation Dialog */}
      {
        showCancelConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2147483647 + 10 // Ensure it's above Overlay
          }}>
            <div style={{
              background: themeMode === 'dark' ? '#1c1c1e' : '#fff',
              borderRadius: '24px',
              padding: '20px',
              maxWidth: '280px',
              width: '85%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              border: borderStyle
            }}>
              <h3 style={{
                margin: '0 0 8px 0',
                fontSize: '16px',
                fontWeight: 700,
                color: themeMode === 'dark' ? '#fff' : '#000',
                textAlign: 'center'
              }}>
                Cancel Payment?
              </h3>
              <p style={{
                margin: '0 0 20px 0',
                fontSize: '13px',
                color: '#8e8e93',
                textAlign: 'center',
                lineHeight: '1.4'
              }}>
                Are you sure you want to cancel this payment?
              </p>
              <div style={{
                display: 'flex',
                gap: '10px'
              }}>
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '100px',
                    border: 'none',
                    background: themeMode === 'dark' ? '#2c2c2e' : '#f2f2f7',
                    color: themeMode === 'dark' ? '#fff' : '#000',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  No
                </button>
                <button
                  onClick={confirmCancel}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '100px',
                    border: 'none',
                    background: '#ff3b30',
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Yes, Cancel
                </button>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
};

export default FozzilPayModal;
